import isEqual from 'react-fast-compare';
import _ from 'underscore';

import {assert} from 'Asserts';
import {Capability} from 'auth/capabilities/CapabilityFeatureConstants';
import {DefaultSchemaPoliciesConfig} from 'auth/PolicyConstants';
import {MonomerNaturalAnalogue, ReviewLifecycleActionType, StudyType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {SampleControlsFeatureLevel} from 'inventory/sample-status/SampleControlsFeatureLevel';
import deepCopy from 'lib/deepCopy';
import {ReviewAction} from 'notebook/reviews/ReviewConstants';
import {ClientBanners} from 'onboarding/ClientBanner';
import {CustomerEnablementType} from 'tenant-comparison/TenantComparisonConstants';
import {RationaleConfigValues} from 'tenants/admin/data-modifications/DataModsConfigConstants';
import {DecimalRoundingStrategy} from 'util/Decimals';

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
const g: any = globalThis;

interface SignatureConfig {
  type: string;
  oktaHost: string;
}

interface CustomBannerConfig {
  bannerKey: ClientBanners;
  showRequiredCheckbox: boolean;
}

interface CustomHelpOption {
  label: string;
  url: string;
}

interface ExportPdfConfig {
  attachResultBlobsInline: boolean;
  includeAuditLog: boolean;
  includeCommentsInReviewHistoryTable: boolean;
  includeFileAttachmentsInline: boolean;
  includeGenbankFileInline: boolean;
  includeMetadataCSV: boolean;
  includeMetadataPDFInline: boolean;
  includeReviewHistoryTable: boolean;
  /** The number of chips to render in table cells, 0 means ALL chips will be rendered */
  maxChipsInTables?: number;
}

interface JsSelfSamplingProfilerConfig {
  perfEventSampleRates: {[eventName: string]: number};
  perfEventMinimumDurationMs: {[eventName: string]: number};
  profilesSampleRate: number;
  tracesSampleRate: number;
}

export interface SnapshotChipsAllowList {
  AnalysisChip?: boolean;
  AnalysisTemplateChip?: boolean;
  AutomationOutputFileChip?: boolean;
  BioentityChip?: boolean;
  BlobLinkTextChip?: boolean;
  BlobLinkPreviewChip?: boolean;
  BlobLinkList?: boolean;
  BlobLinkListInTable?: boolean;
  BulkAssemblyChip?: boolean;
  ConnectionChip?: boolean;
  DatasetChip?: boolean;
  DocumentChip?: boolean;
  EntryChip?: boolean;
  FileChip?: boolean;
  PartyChip?: boolean;
  ProcedureChip?: boolean;
  ProcedureMethodExecutionInstanceChip?: boolean;
  ProcedureRunChip?: boolean;
  ProcessChip?: boolean;
  ProcessOutputChip?: boolean;
  ProcessTaskChip?: boolean;
  RequestChip?: boolean;
  RequestV2SubmissionChip?: boolean;
  RunChip?: boolean;
  SampleDefinitionChip?: boolean;
  SchemaFieldChip?: boolean;
  StageEntryChip?: boolean;
  StorableChip?: boolean;
  StorableSchemaChip?: boolean;
  StudyChip?: boolean;
  TagSchemaChip?: boolean;
  TemplateChip?: boolean;
  TestDefinitionChip?: boolean;
  TestOrderChip?: boolean;
  WorklistChip?: boolean;
  WorksheetChip?: boolean;
}

interface DefaultReviewCommentConfig {
  [ReviewAction.SEND_FOR_REVIEW]: string;
  [ReviewAction.ACCEPT]: string;
  [ReviewAction.COMMENT]: string;
  [ReviewAction.REJECT]: string;
  [ReviewAction.RETRACT]: string;
  [ReviewAction.COMPLETE_CYCLE]: string;
  [ReviewLifecycleActionType.APPROVE]: string;
  [ReviewLifecycleActionType.COMPLETE]: string;
  [ReviewLifecycleActionType.REVIEW]: string;
  [ReviewLifecycleActionType.SELF_REVIEW]: string;
  [ReviewLifecycleActionType.WITNESS]: string;
}

export const DEFAULT_EXPORT_PDF_CONFIG: ExportPdfConfig = {
  attachResultBlobsInline: false,
  includeAuditLog: false,
  includeCommentsInReviewHistoryTable: true,
  includeFileAttachmentsInline: false,
  includeGenbankFileInline: false,
  includeMetadataCSV: false,
  includeMetadataPDFInline: false,
  includeReviewHistoryTable: true,
};

/**
 * Mapping of Structured Table Kind to batched submission batch size.
 */
interface TableSubmissionBatchSizeConfig {
  [key: string]: number;
}

interface TableMaxRowSizeConfig {
  [key: string]: number;
}

/** List of client-facing configs.
 *
 * See corresponding list in benchling/configs/lib/all_options.py
 *
 * NOTE: The persisted value of a config will only be propagated to the client if the ConfigFlag created in
 * all_options.py has `expose_to_client=True`.
 *
 * Usage:
 *
 * @example
 * import BenchlingConfig from 'BenchlingConfig';
 *
 * BenchlingConfig.FLAG
 */
const BenchlingConfig = {
  ANALYSIS_TOOL_FLAGGING_REASON_OPTIONS: (g.ANALYSIS_TOOL_FLAGGING_REASON_OPTIONS || []) as Array<string>,
  ANALYSIS_TOOL_UNFLAGGING_REASON_OPTIONS: (g.ANALYSIS_TOOL_UNFLAGGING_REASON_OPTIONS || []) as Array<string>,
  APP_CORE_ENABLE_FAKE_ITEM_TYPE: (g.APP_CORE_ENABLE_FAKE_ITEM_TYPE ?? false) as boolean,
  APP_CORE_ENABLE_MODERN_DESIGN_SYSTEM: (g.APP_CORE_ENABLE_MODERN_DESIGN_SYSTEM ?? false) as boolean,
  ARCHIVE_ENABLE_REMOVE_BARCODES_OPTION: (g.ARCHIVE_ENABLE_REMOVE_BARCODES_OPTION || false) as boolean,
  ARCHIVE_EXPENDABLE_PURPOSES: (g.ARCHIVE_EXPENDABLE_PURPOSES || []) as Array<string>,
  ARCHIVE_NON_DATA_PURPOSES: (g.ARCHIVE_NON_DATA_PURPOSES || []) as Array<string>,
  ARCHIVE_NON_EXPENDABLE_PURPOSES: (g.ARCHIVE_NON_EXPENDABLE_PURPOSES || []) as Array<string>,
  ARCHIVE_RUN_AND_RESULT_PURPOSES: (g.ARCHIVE_RUN_AND_RESULT_PURPOSES || []) as Array<string>,
  AUDITING_DATA_MODIFICATIONS_COMMENT_OPTIONAL: (g.AUDITING_DATA_MODIFICATIONS_COMMENT_OPTIONAL || false) as boolean,
  AUDITING_DATA_MODIFICATIONS_REASONS_FOR_CHANGE: (g.AUDITING_DATA_MODIFICATIONS_REASONS_FOR_CHANGE || []) as Array<string>,
  AUDITING_DATA_MODIFICATIONS_SETTING: (g.AUDITING_DATA_MODIFICATIONS_SETTING ?? RationaleConfigValues.DISABLED_FOR_TENANT) as RationaleConfigValues,
  AUDITING_DATA_MODIFICATIONS_SKIP_ON_ROW_ADDITION: (g.AUDITING_DATA_MODIFICATIONS_SKIP_ON_ROW_ADDITION || false) as boolean,
  AUDITING_DATA_MODIFICATIONS_V2_INVENTORY: (g.AUDITING_DATA_MODIFICATIONS_V2_INVENTORY || false) as boolean,
  AUDITING_DATA_MODIFICATIONS_V2_NOTEBOOK: (g.AUDITING_DATA_MODIFICATIONS_V2_NOTEBOOK || false) as boolean,
  AUDITING_DATA_MODIFICATIONS_V2_PROJECTS_FOLDERS: (g.AUDITING_DATA_MODIFICATIONS_V2_PROJECTS_FOLDERS || false) as boolean,
  AUDITING_DATA_MODIFICATIONS_V2_REGISTRY: (g.AUDITING_DATA_MODIFICATIONS_V2_REGISTRY || false) as boolean,
  AUDITING_DATA_MODIFICATIONS_V2_WORKFLOWS: (g.AUDITING_DATA_MODIFICATIONS_V2_WORKFLOWS || false) as boolean,
  AUDITING_ENABLE_AGGREGATE_ACTIVITY: (g.AUDITING_ENABLE_AGGREGATE_ACTIVITY || false) as boolean,
  AUDITING_MAX_NUM_SAMPLE_CONTAINER_HISTORY_REPORT_PER_EXPORT: (g.AUDITING_MAX_NUM_SAMPLE_CONTAINER_HISTORY_REPORT_PER_EXPORT || 10) as number,
  AUDITING_PROCEDURE_VALIDATION_OVERRIDE_REASONS: (g.AUDITING_PROCEDURE_VALIDATION_OVERRIDE_REASONS || []) as Array<string>,
  AUTH_CAN_CREATE_AUTHN_RULE: g.AUTH_CAN_CREATE_AUTHN_RULE as boolean,
  AUTH_ENABLE_CONFIGURE_GOAUTH: g.AUTH_ENABLE_CONFIGURE_GOAUTH as boolean,
  AUTH_RECAPTCHA_KEY: (g.AUTH_RECAPTCHA_KEY || null) as string,
  AWS_ACCOUNT_ID: (g.AWS_ACCOUNT_ID || null) as string,
  BENCHLING_APP_CANVAS_POLLING_FREQUENCY_MS: (g.BENCHLING_APP_CANVAS_POLLING_FREQUENCY_MS || 2000) as number,
  BENCHLING_APP_SESSIONS_POLLING_FREQUENCY_MS: (g.BENCHLING_APP_SESSIONS_POLLING_FREQUENCY_MS || 2000) as number,
  BENCHLING_APP_LIMITED_AVAILABILITY_ALLOW_LIST_MAX: (g.BENCHLING_APP_LIMITED_AVAILABILITY_ALLOW_LIST_MAX || 10) as number,
  CAPILLARY_ENABLE_LOGGED_OUT_OPENAPI_SPEC: g.CAPILLARY_ENABLE_LOGGED_OUT_OPENAPI_SPEC as boolean,
  CAPILLARY_ENABLE_NOT_AVAILABLE_DOCS_UI: g.CAPILLARY_ENABLE_NOT_AVAILABLE_DOCS_UI as boolean,
  CAPILLARY_ENABLE_PRE_RELEASE_DOCS_UI: g.CAPILLARY_ENABLE_PRE_RELEASE_DOCS_UI as boolean,
  CAPILLARY_ENABLE_V3_DRAFT_API: g.CAPILLARY_ENABLE_V3_DRAFT_API as boolean,
  CAPILLARY_ENABLE_V3_ALPHA_API: g.CAPILLARY_ENABLE_V3_ALPHA_API as boolean,
  CAPILLARY_ENABLE_V3_BETA_API: g.CAPILLARY_ENABLE_V3_BETA_API as boolean,
  CAPILLARY_ENABLE_V3_UNIFIED_API: g.CAPILLARY_ENABLE_V3_UNIFIED_API as boolean,
  CDN_DOMAIN: (g.CDN_DOMAIN || null) as string | null,
  CHEMISTRY_ENABLE_MOLECULE: (g.CHEMISTRY_ENABLE_MOLECULE || false) as boolean,
  CHEMISTRY_ENABLE_MOLECULE_SALT_FORMS: (g.CHEMISTRY_ENABLE_MOLECULE_SALT_FORMS || false) as boolean,
  CHEMISTRY_ENABLE_STOICHIOMETRY_TABLE: (g.CHEMISTRY_ENABLE_STOICHIOMETRY_TABLE || false) as boolean,
  CHEMISTRY_ENABLE_STOICHIOMETRY_TABLE_SUBMISSION: (g.CHEMISTRY_ENABLE_STOICHIOMETRY_TABLE_SUBMISSION || false) as boolean,
  CHEMISTRY_HIDE_PHYSICOCHEMICAL_PROPERTIES_TAB: (g.CHEMISTRY_HIDE_PHYSICOCHEMICAL_PROPERTIES_TAB || false) as boolean,
  CONFIG_MIGRATION_PRODUCT_LIMIT_TEMPLATES: (g.CONFIG_MIGRATION_PRODUCT_LIMIT_TEMPLATES || {}) as {
    [key: string]: number | string;
  },
  CUSTOMER_ADMIN_EMAIL: (g.CUSTOMER_ADMIN_EMAIL || null) as string | null,
  CUSTOM_URL_PROTOCOL_ALLOW_LIST: (g.CUSTOM_URL_PROTOCOL_ALLOW_LIST || []) as Array<string>,
  DEPLOY_ENVIRONMENT: (g.DEPLOY_ENVIRONMENT || null) as string,
  DEPLOY_NAME: (g.DEPLOY_NAME || null) as string,
  DOMAIN_GRAPH_ENABLE_EXPERIMENTAL_SCHEMAS: (g.DOMAIN_GRAPH_ENABLE_EXPERIMENTAL_SCHEMAS ?? false) as boolean,
  ELN_ALLOW_ADMINS_TO_EDIT_COLUMN_LOCKING_IN_EXECUTION: (g.ELN_ALLOW_ADMINS_TO_EDIT_COLUMN_LOCKING_IN_EXECUTION || false) as boolean,
  ELN_AUTOSAVE_CONFIGURATION: (g.ELN_AUTOSAVE_CONFIGURATION || {
    saveIntervalMs: 4000,
    maximumStalenessIntervalMs: 16000,
  }) as {saveIntervalMs: number; maximumStalenessIntervalMs: number},
  ELN_DISABLE_ENTRY_REVERTS: (g.ELN_DISABLE_ENTRY_REVERTS || false) as boolean,
  ELN_DISABLE_FILL_IN_ONLY_ENTRIES_COLUMN_SORTING: (g.ELN_DISABLE_FILL_IN_ONLY_ENTRIES_COLUMN_SORTING || false) as boolean,
  ELN_DISABLE_PDF_EXPORT_GENERATED_ON_TIMESTAMP: (g.ELN_DISABLE_PDF_EXPORT_GENERATED_ON_TIMESTAMP || false) as boolean,
  ELN_ENABLE_AI_CHECKER: (g.ELN_ENABLE_AI_CHECKER || false) as boolean,
  ELN_ENABLE_AI_DATA_ENTRY: (g.ELN_ENABLE_AI_DATA_ENTRY || false) as boolean,
  ELN_ENABLE_AI_DOCUMENT_GENERATION: (g.ELN_ENABLE_AI_DOCUMENT_GENERATION || false) as boolean,
  ELN_ENABLE_AI_TOOLS: (g.ELN_ENABLE_AI_TOOLS || false) as boolean,
  ELN_ENABLE_ARCHIVING_RESULTS_WITH_ENTRY: (g.ELN_ENABLE_ARCHIVING_RESULTS_WITH_ENTRY || false) as boolean,
  ELN_ENABLE_BATCHED_BULK_LOOKUP_COLUMNS: (g.ELN_ENABLE_BATCHED_BULK_LOOKUP_COLUMNS || false) as boolean,
  ELN_ENABLE_BLANK_ENTRY_CREATION: (g.ELN_ENABLE_BLANK_ENTRY_CREATION || false) as boolean,
  ELN_ENABLE_CHANGE_CUSTOM_COLUMN_DATA_TYPE: (g.ELN_ENABLE_CHANGE_CUSTOM_COLUMN_DATA_TYPE || false) as boolean,
  ELN_ENABLE_COLUMN_TYPE_ICONS: (g.ELN_ENABLE_COLUMN_TYPE_ICONS || false) as boolean,
  ELN_ENABLE_CONTENT_WORKERS: (g.ELN_ENABLE_CONTENT_WORKERS || false) as boolean,
  ELN_ENABLE_CONTENT_WORKERS_PROBABILITY: (g.ELN_ENABLE_CONTENT_WORKERS_PROBABILITY || 1.0) as number,
  ELN_ENABLE_CROSS_STEP_DATA_REFS: (g.ELN_ENABLE_CROSS_STEP_DATA_REFS || false) as boolean,
  ELN_ENABLE_CUSTOM_EVAL_CONDITIONAL_EXECUTION: (g.ELN_ENABLE_CUSTOM_EVAL_CONDITIONAL_EXECUTION || false) as boolean,
  ELN_ENABLE_DESKTOP_SYNC: (g.ELN_ENABLE_DESKTOP_SYNC || false) as boolean,
  ELN_ENABLE_DESKTOP_SYNC_DOWNLOAD: (g.ELN_ENABLE_DESKTOP_SYNC_DOWNLOAD || false) as boolean,
  ELN_ENABLE_DOCUMENT_NOTE_REFERENCES: (g.ELN_ENABLE_DOCUMENT_NOTE_REFERENCES || false) as boolean,
  ELN_ENABLE_EDIT_SCHEMA_AND_TAGS_UNDER_REVIEW: (g.ELN_ENABLE_EDIT_SCHEMA_AND_TAGS_UNDER_REVIEW || false) as boolean,
  ELN_ENABLE_ENTRY_SECTIONS: (g.ELN_ENABLE_ENTRY_SECTIONS ?? true) as boolean,
  ELN_ENABLE_EXTERNAL_GOOGLE_VIEWER: (g.ELN_ENABLE_EXTERNAL_GOOGLE_VIEWER || false) as boolean,
  ELN_ENABLE_EXTERNAL_MSFT_VIEWER: (g.ELN_ENABLE_EXTERNAL_MSFT_VIEWER || false) as boolean,
  ELN_ENABLE_FILL_IN_TEMPLATES: (g.ELN_ENABLE_FILL_IN_TEMPLATES || false) as boolean,
  ELN_ENABLE_IDS_IN_ENTRY_CHIPS: (g.ELN_ENABLE_IDS_IN_ENTRY_CHIPS || false) as boolean,
  ELN_ENABLE_INLINE_LOCATION_SELECTOR: (g.ELN_ENABLE_INLINE_LOCATION_SELECTOR || false) as boolean,
  ELN_ENABLE_INSERT_INVENTORY_TABLE: (g.ELN_ENABLE_INSERT_INVENTORY_TABLE || false) as boolean,
  ELN_ENABLE_INSERT_NEW_REG_TABLE: (g.ELN_ENABLE_INSERT_NEW_REG_TABLE || false) as boolean,
  ELN_ENABLE_INSERT_NEW_RESULTS_TABLE: (g.ELN_ENABLE_INSERT_NEW_RESULTS_TABLE || false) as boolean,
  ELN_ENABLE_INSERT_NEW_TRANSFER_TABLE: (g.ELN_ENABLE_INSERT_NEW_TRANSFER_TABLE || false) as boolean,
  ELN_ENABLE_INSERT_SEARCH: (g.ELN_ENABLE_INSERT_SEARCH || false) as boolean,
  ELN_ENABLE_LEGACY_RUNS_USING_GRAPHQL: (g.ELN_ENABLE_LEGACY_RUNS_USING_GRAPHQL || false) as boolean,
  ELN_ENABLE_LOCALE_AWARE_PARSING_FOR_COPY_PASTE: (g.ELN_ENABLE_LOCALE_AWARE_PARSING_FOR_COPY_PASTE || false) as boolean,
  ELN_ENABLE_LOOKUP_COLUMN_FILTER_BY_ORIGIN: (g.ELN_ENABLE_LOOKUP_COLUMN_FILTER_BY_ORIGIN || false) as boolean,
  ELN_ENABLE_LOOKUP_TABLE_MAPPING: (g.ELN_ENABLE_LOOKUP_TABLE_MAPPING as boolean | undefined) ?? false,
  ELN_ENABLE_METADATA_TEAM_AUTHORS: (g.ELN_ENABLE_METADATA_TEAM_AUTHORS || false) as boolean,
  ELN_ENABLE_PRINT_PREVIEW_SNAPSHOTS: (g.ELN_ENABLE_PRINT_PREVIEW_SNAPSHOTS || false) as boolean,
  ELN_ENABLE_READ_ONLY_MODE: (g.ELN_ENABLE_READ_ONLY_MODE || false) as boolean,
  ELN_ENABLE_REVIRTUALIZATION: (g.ELN_ENABLE_REVIRTUALIZATION || false) as boolean,
  ELN_ENABLE_REVIRTUALIZATION_PROBABILITY: (g.ELN_ENABLE_REVIRTUALIZATION_PROBABILITY || 1.0) as number,
  ELN_ENABLE_SCHEMA_ON_ENTRY_CREATE_MODAL: (g.ELN_ENABLE_SCHEMA_ON_ENTRY_CREATE_MODAL || false) as boolean,
  ELN_ENABLE_SCHEMA_REQUIRED_FOR_REVIEW: (g.ELN_ENABLE_SCHEMA_REQUIRED_FOR_REVIEW || false) as boolean,
  ELN_ENABLE_SIDE_BY_SIDE_IMAGES: (g.ELN_ENABLE_SIDE_BY_SIDE_IMAGES || false) as boolean,
  ELN_ENABLE_SMART_CHIP_SNAPSHOT_CONTEXT: (g.ELN_ENABLE_SMART_CHIP_SNAPSHOT_CONTEXT || false) as boolean,
  ELN_ENABLE_STRUCTURED_TABLE_NEW_RESYNC_MODAL: (g.ELN_ENABLE_STRUCTURED_TABLE_NEW_RESYNC_MODAL || false) as boolean,
  ELN_ENABLE_STRUCTURED_TABLE_SCHEMA_DECIMAL_PRECISION_UNSUBMITTED: (g.ELN_ENABLE_STRUCTURED_TABLE_SCHEMA_DECIMAL_PRECISION_UNSUBMITTED as boolean | undefined) ?? true,
  ELN_ENABLE_TABLE_ADD_ROW_ON_ENTER: (g.ELN_ENABLE_TABLE_ADD_ROW_ON_ENTER ?? false) as boolean,
  ELN_ENABLE_TABLE_DATA_MODIFICATION_SUMMARY: (g.ELN_ENABLE_TABLE_DATA_MODIFICATION_SUMMARY ?? false) as boolean,
  ELN_ENABLE_TABLE_EXPERIMENTAL_HOTKEYS: (g.ELN_ENABLE_TABLE_EXPERIMENTAL_HOTKEYS ?? true) as boolean,
  ELN_ENABLE_TEMPLATE_ARCHIVING: (g.ELN_ENABLE_TEMPLATE_ARCHIVING || false) as boolean,
  ELN_ENABLE_TEMPLATE_PUBLISHING: (g.ELN_ENABLE_TEMPLATE_PUBLISHING || false) as boolean,
  ELN_TEMPLATE_PUBLISHING_RESTRICT_TO_CONTROLLED_NAME: (g.ELN_TEMPLATE_PUBLISHING_RESTRICT_TO_CONTROLLED_NAME || false) as boolean,
  ELN_ENABLE_TEXT_BOX_DATA_MODIFICATIONS: (g.ELN_ENABLE_TEXT_BOX_DATA_MODIFICATIONS || false) as boolean,
  ELN_ENABLE_VIRTUALIZATION_DEBUGGER: (g.ELN_ENABLE_VIRTUALIZATION_DEBUGGER || false) as boolean,
  ELN_ENABLE_XLOOKUP_FORMULA_ERROR_FIX: (g.ELN_ENABLE_XLOOKUP_FORMULA_ERROR_FIX ?? true) as boolean,
  ELN_ENFORCE_REQUIRED_FIELDS_ON_ENTRY_CREATE: (g.ELN_ENFORCE_REQUIRED_FIELDS_ON_ENTRY_CREATE || false) as boolean,
  ELN_ENFORCE_UNIQUE_NAMES: (g.ELN_ENFORCE_UNIQUE_NAMES ?? false) as boolean,
  ELN_ESIGNATURE_CONFIG: (g.ELN_ESIGNATURE_CONFIG || null) as SignatureConfig | null,
  ELN_EXPORT_REVIEW_HISTORY_UI_VERSION: (g.ELN_EXPORT_REVIEW_HISTORY_UI_VERSION || 0) as number,
  ELN_EXPORT_INCLUDE_TENANT_AND_API_ID: (g.ELN_EXPORT_INCLUDE_TENANT_AND_API_ID || false) as boolean,
  ELN_EXPORT_PDF_CONFIG: (g.ELN_EXPORT_PDF_CONFIG || {}) as ExportPdfConfig,
  ELN_EXPORT_USE_APHRODITE_WITH_NODESHELL: (g.ELN_EXPORT_USE_APHRODITE_WITH_NODESHELL || false) as boolean,
  ELN_EXPORT_SNAPSHOT_CHIPS_ALLOW_LIST: (g.ELN_EXPORT_SNAPSHOT_CHIPS_ALLOW_LIST || {}) as SnapshotChipsAllowList,
  ELN_INCLUDE_EXECUTION_TEMPLATES: (g.ELN_INCLUDE_EXECUTION_TEMPLATES ?? true) as boolean,
  ELN_INLINE_TABLE_VIEW_CELL_LIMIT: (g.ELN_INLINE_TABLE_VIEW_CELL_LIMIT || 10_000) as number,
  ELN_INLINE_TABLE_VIEW_ROW_LIMIT: (g.ELN_INLINE_TABLE_VIEW_ROW_LIMIT || 500) as number,
  ELN_INTEGRATION_FIELD_NOT_REQUIRED_ON_ENTRY_CREATE_MODAL: (g.ELN_INTEGRATION_FIELD_NOT_REQUIRED_ON_ENTRY_CREATE_MODAL || false) as boolean,
  ELN_MAX_FILE_UPLOAD_SIZE_MB: g.ELN_MAX_FILE_UPLOAD_SIZE_MB as number,
  ELN_MAX_FILE_UPLOAD_UNLIMITED_SPACE_MB: (g.ELN_MAX_FILE_UPLOAD_UNLIMITED_SPACE_MB ?? g.ELN_MAX_FILE_UPLOAD_SIZE_MB) as number,
  ELN_MAX_PLAIN_TABLE_ROWS: (g.ELN_PLAIN_TABLE_MAXIMUM_ROW_LIMIT || 1000) as number,
  ELN_MAX_STRUCTURED_TABLE_PERSISTED_ROWS: (g.ELN_MAX_STRUCTURED_TABLE_PERSISTED_ROWS || 500) as number,
  ELN_MAX_STRUCTURED_TABLE_ROWS: g.ELN_MAX_STRUCTURED_TABLE_ROWS as number,
  ELN_MAX_STRUCTURED_TABLE_PERSISTED_ROWS_OVERRIDE: (g.ELN_MAX_STRUCTURED_TABLE_PERSISTED_ROWS_OVERRIDE || {}) as TableMaxRowSizeConfig,
  ELN_PARAM_TABLE_LOOKUP_COLUMNS: (g.ELN_PARAM_TABLE_LOOKUP_COLUMNS as boolean | undefined) ?? false,
  ELN_PLAIN_TABLE_ROW_LIMIT_FOR_AUTO_COLLAPSE: g.ELN_PLAIN_TABLE_ROW_LIMIT_FOR_AUTO_COLLAPSE as number,
  ELN_PREVENT_CONCURRENT_NOTEBOOK_SAVES: (g.ELN_PREVENT_CONCURRENT_NOTEBOOK_SAVES || false) as boolean,
  ELN_REALTIME_DOCUMENT_EDITOR_LOCK_DURATION_SECONDS: (g.ELN_REALTIME_DOCUMENT_EDITOR_LOCK_DURATION_SECONDS || 30) as number,
  ELN_RENDER_PRINT_PREVIEW_METADATA: (g.ELN_RENDER_PRINT_PREVIEW_METADATA || false) as boolean,
  ELN_REVIEW_DEFAULT_COMMENTS: (g.ELN_REVIEW_DEFAULT_COMMENTS || {}) as Partial<DefaultReviewCommentConfig>,
  ELN_REVIEW_REJECTION_REQUIRES_COMMENT: (g.ELN_REVIEW_REJECTION_REQUIRES_COMMENT || false) as boolean,
  ELN_REVIEW_REQUIRES_SUCCESSFUL_SNAPSHOT: (g.ELN_REVIEW_REQUIRES_SUCCESSFUL_SNAPSHOT || false) as boolean,
  ELN_RELEASE_DOCUMENT_LOCK_INTERVAL: (g.ELN_RELEASE_DOCUMENT_LOCK_INTERVAL || -1) as number,
  ELN_ROW_LIMIT_FOR_AUTO_COLLAPSE: g.ELN_ROW_LIMIT_FOR_AUTO_COLLAPSE as number,
  ELN_RUN_RESULTS_SORT_BY_INSERTION: (g.ELN_RUN_RESULTS_SORT_BY_INSERTION || false) as boolean,
  ELN_SELF_APPROVE_ENABLED_PROJECTS: (g.ELN_SELF_APPROVE_ENABLED_PROJECTS || []) as Array<string>,
  ELN_SEND_REVIEW_REQUIRES_SUBMITTED_TABLES: (g.ELN_SEND_REVIEW_REQUIRES_SUBMITTED_TABLES || false) as boolean,
  ELN_SHADOW_EVALUATION_SAMPLE_RATE: (g.ELN_SHADOW_EVALUATION_SAMPLE_RATE || 0) as number,
  ELN_TABLE_IME_SUPPORT: (g.ELN_TABLE_IME_SUPPORT || false) as boolean,
  ELN_TABLE_SHOW_MULTIPLE_CELL_CONDITIONS: (g.ELN_TABLE_SHOW_MULTIPLE_CELL_CONDITIONS ?? true) as boolean,
  ELN_TABLE_SUBMISSION_CUSTOM_BATCH_SIZES: (g.ELN_TABLE_SUBMISSION_CUSTOM_BATCH_SIZES || {}) as TableSubmissionBatchSizeConfig,
  ELN_TABLE_SUBMISSION_GLOBAL_BATCH_SIZES: (g.ELN_TABLE_SUBMISSION_GLOBAL_BATCH_SIZES || {}) as TableSubmissionBatchSizeConfig,
  ELN_USE_NOTE_PART_LEVEL_SAVING: (g.ELN_USE_NOTE_PART_LEVEL_SAVING || false) as boolean,
  ELN_USE_NRS_LOCALE_IN_EVALUATION: (g.ELN_USE_NRS_LOCALE_IN_EVALUATION ?? true) as boolean,
  ELN_VALIDATE_NOTE_PART_LEVEL_SAVING: (g.ELN_VALIDATE_NOTE_PART_LEVEL_SAVING || false) as boolean,
  ELN_VIRTUALIZED_SEARCH_MAX_RESULTS: (g.ELN_VIRTUALIZED_SEARCH_MAX_RESULTS || -1) as number,
  ELN_ENABLE_GOOGLE_DRIVE_FILE_PICKER: (g.ELN_ENABLE_GOOGLE_DRIVE_FILE_PICKER ?? false) as boolean,
  ELN_GDRIVE_OAUTH_CLIENT_ID: (g.ELN_GDRIVE_OAUTH_CLIENT_ID ?? '') as string,
  ENABLE_ANALYSIS_TOOL_ALL_TYPE_QUERY_OPTION: (g.ENABLE_ANALYSIS_TOOL_ALL_TYPE_QUERY_OPTION || false) as boolean,
  ENABLE_ANALYSIS_TOOL_CONNECTION_DATASET_CREATION: (g.ENABLE_ANALYSIS_TOOL_CONNECTION_DATASET_CREATION || false) as boolean,
  ENABLE_ANALYSIS_TOOL_EDITABLE_TEMPLATES: (g.ENABLE_ANALYSIS_TOOL_EDITABLE_TEMPLATES ?? false) as boolean,
  ENABLE_ANALYSIS_TOOL_EXTRACT_DATA_FRAME_VALUE_STEP: (g.ENABLE_ANALYSIS_TOOL_EXTRACT_DATA_FRAME_VALUE_STEP || false) as boolean,
  ENABLE_ANALYSIS_TOOL_INTERACTIVE_FLOWCHART_EXPERIMENTAL: (g.ENABLE_ANALYSIS_TOOL_INTERACTIVE_FLOWCHART_EXPERIMENTAL ?? false) as boolean,
  ENABLE_ANALYSIS_TOOL_REFRESH_DATA_BUTTON: (g.ENABLE_ANALYSIS_TOOL_REFRESH_DATA_BUTTON ?? false) as boolean,
  ENABLE_ANALYSIS_TOOL_REGRESSIONS: (g.ENABLE_ANALYSIS_TOOL_REGRESSIONS ?? false) as boolean,
  ENABLE_ANALYSIS_TOOL_TEMPLATES: (g.ENABLE_ANALYSIS_TOOL_TEMPLATES || false) as boolean,
  ENABLE_ANALYSIS_TOOL_WITHOUT_INSIGHTS: (g.ENABLE_ANALYSIS_TOOL_WITHOUT_INSIGHTS ?? true) as boolean,
  ENABLE_ANTIVIRUS_SCANNING: (g.ENABLE_ANTIVIRUS_SCANNING ?? false) as boolean,
  ENABLE_ASYNC_BLAST_SEARCH: (g.ENABLE_ASYNC_BLAST_SEARCH || false) as boolean,
  ENABLE_AUTHORS: (g.ENABLE_AUTHORS || false) as boolean,
  ENABLE_AUTHOR_REVIEW_IN_ENTRY_REVIEW_PROCESSES: (g.ENABLE_AUTHOR_REVIEW_IN_ENTRY_REVIEW_PROCESSES || false) as boolean,
  ENABLE_AUTO_POPULATE_COLLABORATORS: (g.ENABLE_AUTO_POPULATE_COLLABORATORS || false) as boolean,
  ENABLE_BASE26_ID_NAME_TEMPLATE_PART: (g.ENABLE_BASE26_ID_NAME_TEMPLATE_PART || false) as boolean,
  ENABLE_BATCHES: (g.ENABLE_BATCHES || false) as boolean,
  ENABLE_BATCH_GUIDE_DESIGN: (g.ENABLE_BATCH_GUIDE_DESIGN || false) as boolean,
  ENABLE_BATCH_SEQUENCE_IMPORT: (g.ENABLE_BATCH_SEQUENCE_IMPORT || false) as boolean,
  ENABLE_BENCHLING_APPS: (g.ENABLE_BENCHLING_APPS || false) as boolean,
  ENABLE_BENCHLING_APPS_AUTHENTICATION_SERVICE: (g.ENABLE_BENCHLING_APPS_AUTHENTICATION_SERVICE || false) as boolean,
  ENABLE_BENCHLING_APPS_CANVAS_BUILDER: (g.ENABLE_BENCHLING_APPS_CANVAS_BUILDER || false) as boolean,
  ENABLE_BENCHLING_APPS_CREATE_INSTALL_SEPARATION: (g.ENABLE_BENCHLING_APPS_CREATE_INSTALL_SEPARATION || false) as boolean,
  ENABLE_BENCHLING_APPS_DEFINITION_SQLA: (g.ENABLE_BENCHLING_APPS_DEFINITION_SQLA ?? true) as boolean,
  ENABLE_BENCHLING_APPS_GLOBAL_DEFINITIONS: (g.ENABLE_BENCHLING_APPS_GLOBAL_DEFINITIONS || false) as boolean,
  ENABLE_BENCHLING_APPS_TABULAR_CONFIG: (g.ENABLE_BENCHLING_APPS_TABULAR_CONFIG || false) as boolean,
  ENABLE_BENCHLING_APPS_WEBHOOK_HEADERS: (g.ENABLE_BENCHLING_APPS_WEBHOOK_HEADERS || false) as boolean,
  ENABLE_BENCHLING_CONNECT_ATTACH_FILES_TO_RUNS: (g.ENABLE_BENCHLING_CONNECT_ATTACH_FILES_TO_RUNS || false) as boolean,
  ENABLE_BENCHLING_CONNECT_FILE_TESTING_IN_CONNECTION: (g.ENABLE_BENCHLING_CONNECT_FILE_TESTING_IN_CONNECTION || false) as boolean,
  ENABLE_BENCHLING_CONNECT_JSON_MAPPER_CONFIG: (g.ENABLE_BENCHLING_CONNECT_JSON_MAPPER_CONFIG || false) as boolean,
  ENABLE_BENCHLING_SUPPORT_BEARER_TOKEN_UI: (g.ENABLE_BENCHLING_SUPPORT_BEARER_TOKEN_UI || false) as boolean,
  ENABLE_BLAST_SEARCH: (g.ENABLE_BLAST_SEARCH || false) as boolean,
  ENABLE_BLAST_SEARCH_MULTI_QUERY_INPUT: (g.ENABLE_BLAST_SEARCH_MULTI_QUERY_INPUT || false) as boolean,
  ENABLE_BULK_ANTIBODY: (g.ENABLE_BULK_ANTIBODY || false) as boolean,
  ENABLE_BULK_ASSEMBLY: (g.ENABLE_BULK_ASSEMBLY || false) as boolean,
  ENABLE_COLLAB_EDIT: false as boolean, // This is always disabled - backend has been deleted and frontend has yet to be deleted.
  ENABLE_CONSISTENT_REVIEW_SUMMARY_COUNT: (g.ENABLE_CONSISTENT_REVIEW_SUMMARY_COUNT || false) as boolean,
  ENABLE_CRISPR: (g.ENABLE_CRISPR || false) as boolean,
  ENABLE_CRISPR_IA: (g.ENABLE_CRISPR_IA || false) as boolean,
  ENABLE_DELETE: (g.ENABLE_DELETE || false) as boolean,
  ENABLE_ELN_AUTHOR_SELF_APPROVE: (g.ENABLE_ELN_AUTHOR_SELF_APPROVE || false) as boolean,
  ENABLE_ELN_ESIGNATURE: (g.ENABLE_ELN_ESIGNATURE || false) as boolean,
  ENABLE_ELN_ESIGNATURE_BROADCAST_CHANNEL: (g.ENABLE_ELN_ESIGNATURE_BROADCAST_CHANNEL || false) as boolean,
  ENABLE_ELN_REVIEW_COMMENTS: (g.ENABLE_ELN_REVIEW_COMMENTS || false) as boolean,
  ENABLE_ELN_REVIEW_CRITERIA: (g.ENABLE_ELN_REVIEW_CRITERIA || false) as boolean,
  ENABLE_ELN_REVIEW_SNAPSHOTS: (g.ENABLE_ELN_REVIEW_SNAPSHOTS || false) as boolean,
  ENABLE_ENTRY_REVIEW_PROCESSES: (g.ENABLE_ENTRY_REVIEW_PROCESSES || false) as boolean,
  ENABLE_ENTRY_SCHEMAS: (g.ENABLE_ENTRY_SCHEMAS || false) as boolean,
  ENABLE_ENTRY_SCHEMA_REMOVE_FIELDS: (g.ENABLE_ENTRY_SCHEMA_REMOVE_FIELDS || false) as boolean,
  ENABLE_EQUIPMENT: (g.ENABLE_EQUIPMENT || false) as boolean,
  ENABLE_EXTENSION_TYPE_AGENT_SKILLS: (g.ENABLE_EXTENSION_TYPE_AGENT_SKILLS || false) as boolean,
  ENABLE_EXTENSION_TYPE_MODELS: (g.ENABLE_EXTENSION_TYPE_MODELS || false) as boolean,
  ENABLE_EXTENSION_MODEL_VERSIONS: (g.ENABLE_EXTENSION_MODEL_VERSIONS || false) as boolean,
  ENABLE_EXTENSION_DIRECTORY: (g.ENABLE_EXTENSION_DIRECTORY || false) as boolean,
  ENABLE_EXPORTS: (g.ENABLE_EXPORTS || false) as boolean,
  ENABLE_EXTERNAL_ANALYTICS_UI: (g.ENABLE_EXTERNAL_ANALYTICS_UI || false) as boolean,
  ENABLE_EXTERNAL_TENANT_COMPARISON: (g.ENABLE_EXTERNAL_TENANT_COMPARISON || CustomerEnablementType.HIDDEN) as CustomerEnablementType,
  ENABLE_FRONTEND_METRICS: Boolean(g.ENABLE_FRONTEND_METRICS) || false,
  ENABLE_FROZEN_CHIPS: (g.ENABLE_FROZEN_CHIPS || false) as boolean,
  ENABLE_GAINSIGHT_PX: (g.ENABLE_GAINSIGHT_PX || false) as boolean,
  ENABLE_GETTING_STARTED_FLOW: (g.ENABLE_GETTING_STARTED_FLOW || false) as boolean,
  ENABLE_GLOBAL_INVITE_SCIENTISTS: (g.ENABLE_GLOBAL_INVITE_SCIENTISTS || false) as boolean,
  ENABLE_HOMEPAGE_CANVAS_REIMPLEMENTED: (g.ENABLE_HOMEPAGE_CANVAS_REIMPLEMENTED || false) as boolean,
  ENABLE_HOVERING_FORMAT_TOOLBAR: (g.ENABLE_HOVERING_FORMAT_TOOLBAR || false) as boolean,
  ENABLE_IN_APP_SUPPORT: (g.ENABLE_IN_APP_SUPPORT || false) as boolean,
  ENABLE_INSIGHTS_LABS: (g.ENABLE_INSIGHTS_LABS || false) as boolean,
  ENABLE_INSIGHTS_LABS_PAGE: (g.ENABLE_INSIGHTS_LABS_PAGE || false) as boolean,
  ENABLE_LAB_AUTOMATION: (g.ENABLE_LAB_AUTOMATION || false) as boolean,
  ENABLE_LAB_AUTOMATION_BULK_UPLOAD_OUTPUT_FILES: (g.ENABLE_LAB_AUTOMATION_BULK_UPLOAD_OUTPUT_FILES || false) as boolean,
  ENABLE_LAB_AUTOMATION_FILE_CHUNKING: (g.ENABLE_LAB_AUTOMATION_FILE_CHUNKING || false) as boolean,
  ENABLE_LEGACY_ADMIN_DASHBOARD_PAGES: (g.ENABLE_LEGACY_ADMIN_DASHBOARD_PAGES || false) as boolean,
  ENABLE_MIXTURE_PREP_ENTRY_EVALUATION: (g.ENABLE_MIXTURE_PREP_ENTRY_EVALUATION || false) as boolean,
  ENABLE_MULTIPLE_SEARCH_RESULT_VIEWS: (g.ENABLE_MULTIPLE_SEARCH_RESULT_VIEWS || false) as boolean,
  ENABLE_NEW_RESULTS: (g.ENABLE_NEW_RESULTS || false) as boolean,
  ENABLE_NUE_MODULES: (g.ENABLE_NUE_MODULES || false) as boolean,
  ENABLE_ONBOARDING_ROLE_INFO: (g.ENABLE_ONBOARDING_ROLE_INFO || false) as boolean,
  ENABLE_PATENTSAFE: (g.ENABLE_PATENTSAFE || false) as boolean,
  ENABLE_POLICY_UI: (g.ENABLE_POLICY_UI || false) as boolean,
  ENABLE_PROFILES: (g.ENABLE_PROFILES || false) as boolean,
  ENABLE_PROJECTS_REQUIRED_FOR_NON_LOCATION_STORABLES: (g.ENABLE_PROJECTS_REQUIRED_FOR_NON_LOCATION_STORABLES || false) as boolean,
  ENABLE_PROJECTS_REQUIRED_FOR_REGISTRY_FILES: (g.ENABLE_PROJECTS_REQUIRED_FOR_REGISTRY_FILES || false) as boolean,
  ENABLE_PROMO_CODES: (g.ENABLE_PROMO_CODES || false) as boolean,
  ENABLE_PROTOCOLS: (g.ENABLE_PROTOCOLS || false) as boolean,
  ENABLE_PROTOCOLS_IN_ENTRIES: (g.ENABLE_PROTOCOLS_IN_ENTRIES || false) as boolean,
  ENABLE_PUBLIC_FOLDERS: (g.ENABLE_PUBLIC_FOLDERS || false) as boolean,
  ENABLE_PULSES: (g.ENABLE_PULSES || false) as boolean,
  ENABLE_REGISTRATION_TABLE_CHECKBOXES: (g.ENABLE_REGISTRATION_TABLE_CHECKBOXES || false) as boolean,
  ENABLE_REGISTRY: (g.ENABLE_REGISTRY || false) as boolean,
  ENABLE_REGISTRY_LABELING_STRATEGY_KEEP_NAMES: (g.ENABLE_REGISTRY_LABELING_STRATEGY_KEEP_NAMES || false) as boolean,
  ENABLE_REGISTRY_LABELING_STRATEGY_REPLACE_ID_AND_NAME_FROM_PARTS: (g.ENABLE_REGISTRY_LABELING_STRATEGY_REPLACE_ID_AND_NAME_FROM_PARTS || false) as boolean,
  ENABLE_REQUESTS: (g.ENABLE_REQUESTS || false) as boolean,
  ENABLE_REQUEST_GENOMES: (g.ENABLE_REQUEST_GENOMES || false) as boolean,
  ENABLE_REQUIRED_PARENT_STORABLE: (g.ENABLE_REQUIRED_PARENT_STORABLE || false) as boolean,
  ENABLE_RETRACT_ACTIVE_REVIEWS_ON_REVIEW_PROCESS_UPDATE: (g.ENABLE_RETRACT_ACTIVE_REVIEWS_ON_REVIEW_PROCESS_UPDATE || false) as boolean,
  ENABLE_RUNS: (g.ENABLE_RUNS || false) as boolean,
  ENABLE_SCHEMA_CREATE_AS_OLIGO: (g.ENABLE_SCHEMA_CREATE_AS_OLIGO || false) as boolean,
  ENABLE_SCRATCH_PLATES: (g.ENABLE_SCRATCH_PLATES || false) as boolean,
  ENABLE_SEGMENT_ANALYTICS_JS: (g.ENABLE_SEGMENT_ANALYTICS_JS || false) as boolean,
  ENABLE_SHARE_LINK: (g.ENABLE_SHARE_LINK || false) as boolean,
  ENABLE_SHOW_USERNAME_IN_WORKSPACE: (g.ENABLE_SHOW_USERNAME_IN_WORKSPACE || false) as boolean,
  ENABLE_SIGHTGLASS_RUNS_SEARCH: (g.ENABLE_SIGHTGLASS_RUNS_SEARCH || false) as boolean,
  ENABLE_SIGHTGLASS_SHARED_SAVED_SEARCH: (g.ENABLE_SIGHTGLASS_SHARED_SAVED_SEARCH || false) as boolean,
  ENABLE_SIGHTGLASS_V3_GROUP_BY: (g.ENABLE_SIGHTGLASS_V3_GROUP_BY || false) as boolean,
  ENABLE_SIGNED_OUT_SHARE_LINK_USAGE: (g.ENABLE_SIGNED_OUT_SHARE_LINK_USAGE || false) as boolean,
  ENABLE_STORAGE: (g.ENABLE_STORAGE || false) as boolean,
  ENABLE_STRUCTURED_TABLE_VALIDATION: (g.ENABLE_STRUCTURED_TABLE_VALIDATION || false) as boolean,
  ENABLE_SUBMITTED_TABLE_DELETION_PREVENTION: (g.ENABLE_SUBMITTED_TABLE_DELETION_PREVENTION || false) as boolean,
  ENABLE_SUDO_REVIEWS: (g.ENABLE_SUDO_REVIEWS || false) as boolean,
  ENABLE_SYNCER_DEBUGGING: (g.ENABLE_SYNCER_DEBUGGING || false) as boolean,
  ENABLE_SYNCER_METRICS: (g.ENABLE_SYNCER_METRICS || false) as boolean,
  ENABLE_SYNCER_SIZE_METRICS: (g.ENABLE_SYNCER_SIZE_METRICS || false) as boolean,
  ENABLE_TABLE_FIXED_HEIGHT_AS_DEFAULT: (g.ENABLE_TABLE_FIXED_HEIGHT_AS_DEFAULT || false) as boolean,
  ENABLE_TEAMS: (g.ENABLE_TEAMS || false) as boolean,
  ENABLE_TEAM_REVIEW_IN_ENTRY_REVIEW_PROCESSES: (g.ENABLE_TEAM_REVIEW_IN_ENTRY_REVIEW_PROCESSES || false) as boolean,
  ENABLE_TENANT_ADMIN_CONSOLE: (g.ENABLE_TENANT_ADMIN_CONSOLE || false) as boolean,
  ENABLE_TEMPLATE_COLLECTIONS_IN_WORKSPACE: (g.ENABLE_TEMPLATE_COLLECTIONS_IN_WORKSPACE || false) as boolean,
  ENABLE_TEMPLATE_COLLECTIONS_IN_WORKSPACE_BETA_BANNER: (g.ENABLE_TEMPLATE_COLLECTIONS_IN_WORKSPACE_BETA_BANNER || false) as boolean,
  ENABLE_USER_CREATED_WAREHOUSE_CREDENTIALS: (g.ENABLE_USER_CREATED_WAREHOUSE_CREDENTIALS || false) as boolean,
  ENABLE_USER_PROVISIONING: (g.ENABLE_USER_PROVISIONING || false) as boolean,
  ENABLE_USER_SIGNUPS: (g.ENABLE_USER_SIGNUPS || false) as boolean,
  ENABLE_WAREHOUSE: (g.ENABLE_WAREHOUSE || false) as boolean,
  ENABLE_WORKSHEET_REVIEWS: (g.ENABLE_WORKSHEET_REVIEWS || false) as boolean,
  ENABLE_WORKSHEET_REVIEW_SNAPSHOTS: (g.ENABLE_WORKSHEET_REVIEW_SNAPSHOTS || false) as boolean,
  ENABLE_ZENDESK_GUIDE: (g.ENABLE_ZENDESK_GUIDE || false) as boolean,
  ENABLE_ZENDESK_MESSAGING: (g.ENABLE_ZENDESK_MESSAGING || false) as boolean,
  EVENTS_ENABLE_UI: (g.EVENTS_ENABLE_UI || false) as boolean,
  FILE_TYPE_BLOCK_LIST: (g.FILE_TYPE_BLOCK_LIST || []) as Array<string>,
  GROWTH_ENABLE_CLONING_SHARED_FILES_INTO_NEW_ACCOUNT: (g.GROWTH_ENABLE_CLONING_SHARED_FILES_INTO_NEW_ACCOUNT ?? false) as boolean,
  GROWTH_ENABLE_ORGANIZATION_RECOMMENDATIONS: (g.GROWTH_ENABLE_ORGANIZATION_RECOMMENDATIONS ?? false) as boolean,
  IDENTITY_ENABLE_AUTHENTICATION_OPTION_AUDIT_EXPORT: (g.IDENTITY_ENABLE_AUTHENTICATION_OPTION_AUDIT_EXPORT ?? false) as boolean,
  IDENTITY_ENABLE_AUTO_ADD_TEAM_COLLABORATORS: (g.IDENTITY_ENABLE_AUTO_ADD_TEAM_COLLABORATORS || false) as boolean,
  IDENTITY_ENABLE_CAPABILITY_GATING: (g.IDENTITY_ENABLE_CAPABILITY_GATING || false) as boolean,
  IDENTITY_ENABLE_CAPABILITY_PROVISIONING: (g.IDENTITY_ENABLE_CAPABILITY_PROVISIONING || false) as boolean,
  IDENTITY_ENABLE_CAPABILITY_PROVISIONING_READ_ACCESS: (g.IDENTITY_ENABLE_CAPABILITY_PROVISIONING_READ_ACCESS || false) as boolean,
  IDENTITY_ENABLE_GATING_AND_PROVISIONING_IN_DEVELOPMENT_CAPABILITIES: (g.IDENTITY_ENABLE_GATING_AND_PROVISIONING_IN_DEVELOPMENT_CAPABILITIES || false) as boolean,
  IDENTITY_ENABLE_IP_ALLOWLIST_PAGE: (g.IDENTITY_ENABLE_IP_ALLOWLIST_PAGE || false) as boolean,
  IDENTITY_HAS_RUN_CAPABILITY_MANAGEMENT_MIGRATION: (g.IDENTITY_HAS_RUN_CAPABILITY_MANAGEMENT_MIGRATION || false) as boolean,
  IDENTITY_RELEASED_CAPABILITIES: (g.IDENTITY_RELEASED_CAPABILITIES || []) as Array<Capability>,
  IDENTITY_ENABLE_TAC_FILTERED_USER_EXPORT: (g.IDENTITY_ENABLE_TAC_FILTERED_USER_EXPORT ?? true) as boolean,
  IDENTITY_ENABLE_AUTHENTICATION_OPTIONS_PAGE: (g.IDENTITY_ENABLE_AUTHENTICATION_OPTIONS_PAGE || false) as boolean,
  IDENTITY_ENABLE_OAUTH_AUTHORIZATION_CODE_FLOW: (g.IDENTITY_ENABLE_OAUTH_AUTHORIZATION_CODE_FLOW || false) as boolean,
  INSIGHTS_ENABLE_AI_SQL_ASSISTANT: (g.INSIGHTS_ENABLE_AI_SQL_ASSISTANT || false) as boolean,
  INSIGHTS_ENABLE_COPY_BUTTON_PER_ROW: (g.INSIGHTS_ENABLE_COPY_BUTTON_PER_ROW || false) as boolean,
  INSIGHTS_ENABLE_POINT_MASKING: (g.INSIGHTS_ENABLE_POINT_MASKING || false) as boolean,
  INSIGHTS_ENABLE_SHAPASH_CONTROLLER_TABLES: (g.INSIGHTS_ENABLE_SHAPASH_CONTROLLER_TABLES || false) as boolean,
  INSIGHTS_MAX_CHIP_NAMES_TO_RESOLVE: (g.INSIGHTS_MAX_CHIP_NAMES_TO_RESOLVE || 500) as number, // keep in sync with default_value in all_options.py
  INSIGHTS_MAX_CONCURRENT_QUERIES_PER_BROWSER_TAB: (g.INSIGHTS_MAX_CONCURRENT_QUERIES_PER_BROWSER_TAB || 12) as number, // keep in sync with default_value in all_options.py
  INSIGHTS_MAX_CONCURRENT_REGRESSIONS_PER_BROWSER_TAB: (g.INSIGHTS_MAX_CONCURRENT_REGRESSIONS_PER_BROWSER_TAB || 12) as number, // keep in sync with default_value in all_options.py
  INSTRUMENTS_ENABLE: (g.INSTRUMENTS_ENABLE || false) as boolean,
  INSTRUMENTS_ENABLE_CONNECTION_DETAILS_POLLING: (g.INSTRUMENTS_ENABLE_CONNECTION_DETAILS_POLLING || false) as boolean,
  INSTRUMENTS_ENABLE_LINUX_INSTALLER_FLOW: (g.INSTRUMENTS_ENABLE_LINUX_INSTALLER_FLOW || false) as boolean,
  INSTRUMENTS_ENABLE_OFFLINE_CONNECTION_DEMOS: (g.INSTRUMENTS_ENABLE_OFFLINE_CONNECTION_DEMOS || false) as boolean,
  INSTRUMENTS_ENABLE_S3_IAM_ROLE_ACCESS_METHOD: (g.INSTRUMENTS_ENABLE_S3_IAM_ROLE_ACCESS_METHOD || false) as boolean,
  OPENTRONS_ENABLE_PIPELINE_STEP: (g.OPENTRONS_ENABLE_PIPELINE_STEP || false) as boolean,
  INTERNAL_ADMIN_CONFIG_WARNING_TEXT: (g.INTERNAL_ADMIN_CONFIG_WARNING_TEXT || null) as string | null,
  INVENTORY_CUSTOM_PLATE_SIZES: (g.INVENTORY_CUSTOM_PLATE_SIZES || []) as Array<{
    rows: number;
    columns: number;
  }>,
  INVENTORY_DISABLE_ENFORCE_TRANSFER_QUANTITIES: (g.INVENTORY_DISABLE_ENFORCE_TRANSFER_QUANTITIES || false) as boolean,
  INVENTORY_ENABLE_AI_PLATE_MAP_ANNOTATION_ASSISTANT: (g.INVENTORY_ENABLE_AI_PLATE_MAP_ANNOTATION_ASSISTANT || false) as boolean,
  INVENTORY_ENABLE_BOOLEAN_FIELD_IN_SCHEMAS: (g.INVENTORY_ENABLE_BOOLEAN_FIELD_IN_SCHEMAS || false) as boolean,
  INVENTORY_ENABLE_EXPIRATION_DATE: (g.INVENTORY_ENABLE_EXPIRATION_DATE || false) as boolean,
  INVENTORY_ENABLE_FREEZE_THAW: (g.INVENTORY_ENABLE_FREEZE_THAW || false) as boolean,
  INVENTORY_ENABLE_GRID_VIEW_ENTITY_SCHEMA_FIELDS: (g.INVENTORY_ENABLE_GRID_VIEW_ENTITY_SCHEMA_FIELDS || false) as boolean,
  INVENTORY_ENABLE_HIGHER_PRECISION: (g.INVENTORY_ENABLE_HIGHER_PRECISION || false) as boolean,
  INVENTORY_ENABLE_REQUIRE_CONTENTS_FOR_TRANSFERS: (g.INVENTORY_ENABLE_REQUIRE_CONTENTS_FOR_TRANSFERS ?? true) as boolean,
  INVENTORY_ENABLE_SAMPLE_CONTROL_FEATURES: (g.INVENTORY_ENABLE_SAMPLE_CONTROL_FEATURES || SampleControlsFeatureLevel.OFF) as string, // keep in sync with default_value in all_options.py
  INVENTORY_ENABLE_SOLID_UNITS_FOR_CONTAINER: (g.INVENTORY_ENABLE_SOLID_UNITS_FOR_CONTAINER || false) as boolean,
  INVENTORY_ENABLE_SUMMARY_ON_ENTITY_SEARCH: (g.INVENTORY_ENABLE_SUMMARY_ON_ENTITY_SEARCH || false) as boolean,
  INVENTORY_ENABLE_ZPL_LABEL_PREVIEW: (g.INVENTORY_ENABLE_ZPL_LABEL_PREVIEW || false) as boolean,
  INVENTORY_ZEBRA_BROWSER_PRINT_HTTP_PORT_OVERRIDE: (g.INVENTORY_ZEBRA_BROWSER_PRINT_HTTP_PORT_OVERRIDE ?? null) as number | null,
  INVENTORY_ENABLE_REQUIRED_FIELDS_FOR_FIXED_PLATE_CONTAINER_SCHEMAS: (g.INVENTORY_ENABLE_REQUIRED_FIELDS_FOR_FIXED_PLATE_CONTAINER_SCHEMAS || false) as boolean,
  INVITE_EMAIL: (g.INVITE_EMAIL || 'invite@benchling.com') as string,
  INVITE_SCIENTISTS_EXTERNALLY: (g.INVITE_SCIENTISTS_EXTERNALLY || false) as boolean,
  LAB_AUTOMATION_ALLOW_EDITING_API_RUNS: g.LAB_AUTOMATION_ALLOW_EDITING_API_RUNS as boolean,
  LAB_AUTOMATION_CASCADE_OUTPUT_FILE_ERRORS_TO_RELATED_ROWS: g.LAB_AUTOMATION_CASCADE_OUTPUT_FILE_ERRORS_TO_RELATED_ROWS as boolean,
  LAB_AUTOMATION_ENABLE_PLATE_TRANSFER: (g.LAB_AUTOMATION_ENABLE_PLATE_TRANSFER || false) as boolean,
  LAB_AUTOMATION_ENABLE_TRANSFORM_PREVIEWS: (g.LAB_AUTOMATION_ENABLE_TRANSFORM_PREVIEWS || false) as boolean,
  LAB_AUTOMATION_LOCK_RUN_AFTER_CREATION: g.LAB_AUTOMATION_LOCK_RUN_AFTER_CREATION as boolean,
  LAB_AUTOMATION_LOCK_RUN_AFTER_INPUT_FILE_GENERATED: g.LAB_AUTOMATION_LOCK_RUN_AFTER_INPUT_FILE_GENERATED as boolean,
  LAB_AUTOMATION_LOCK_RUN_AFTER_OUTPUT_FILE_PROCESSED: g.LAB_AUTOMATION_LOCK_RUN_AFTER_OUTPUT_FILE_PROCESSED as boolean,
  LAB_AUTOMATION_OUTPUT_FILE_CONFIG_UI_SPREADSHEET_UPLOAD: g.LAB_AUTOMATION_OUTPUT_FILE_CONFIG_UI_SPREADSHEET_UPLOAD as boolean,
  LAUNCHDARKLY_CLIENT_ID: g.LAUNCHDARKLY_CLIENT_ID as string,
  LAUNCHDARKLY_CLIENT_URL: g.LAUNCHDARKLY_CLIENT_URL as string | null,
  LEGACY_WORKFLOWS_ALLOW_DELETION: (g.LEGACY_WORKFLOWS_ALLOW_DELETION ?? true) as boolean,
  LEGACY_WORKFLOWS_ENABLE: (g.LEGACY_WORKFLOWS_ENABLE || false) as boolean,
  LEGACY_WORKFLOWS_ENABLE_BULK_SEND_STAGE_ENTRIES_FOR_REVIEW: (g.LEGACY_WORKFLOWS_ENABLE_BULK_SEND_STAGE_ENTRIES_FOR_REVIEW || false) as boolean,
  LEGACY_WORKFLOWS_ENABLE_STAGE_RUN_RENAME: (g.LEGACY_WORKFLOWS_ENABLE_STAGE_RUN_RENAME || false) as boolean,
  LLM_AI_PROVIDER_HIDDEN: (g.LLM_AI_PROVIDER_HIDDEN || false) as boolean,
  LLM_ENABLE_OPENAI: (g.LLM_ENABLE_OPENAI || false) as boolean,
  LLM_ENABLE_GOOGLE_CLOUD: (g.LLM_ENABLE_GOOGLE_CLOUD || false) as boolean,
  LLM_ENABLE_AGENT_SWITCH: (g.LLM_ENABLE_AGENT_SWITCH || false) as boolean,
  LLM_ENABLE_AI_ANALYSIS_AGENT: (g.LLM_ENABLE_AI_ANALYSIS_AGENT || false) as boolean,
  LLM_ENABLE_AI_DATA_CAPTURE_ASSISTANT: (g.LLM_ENABLE_AI_DATA_CAPTURE_ASSISTANT || false) as boolean,
  LLM_ENABLE_AI_DEEP_RESEARCH: (g.LLM_ENABLE_AI_DEEP_RESEARCH || false) as boolean,
  LLM_ENABLE_AI_DEEP_RESEARCH_PUBLIC_DATA: (g.LLM_ENABLE_AI_DEEP_RESEARCH_PUBLIC_DATA || false) as boolean,
  LLM_ENABLE_ASSISTANT_AGENT: (g.LLM_ENABLE_ASSISTANT_AGENT || false) as boolean,
  LLM_ENABLE_ASSISTANT_AGENT_ENTRY_EDITING: (g.LLM_ENABLE_ASSISTANT_AGENT_ENTRY_EDITING || false) as boolean,
  LLM_ENABLE_CO_SCIENTIST: (g.LLM_ENABLE_CO_SCIENTIST || false) as boolean,
  LLM_ENABLE_COMPOSE_OVER_ENTRIES: (g.LLM_ENABLE_COMPOSE_OVER_ENTRIES || false) as boolean,
  LLM_ENABLE_PUBLIC_AI_TRIAL_TENANT: (g.LLM_ENABLE_PUBLIC_AI_TRIAL_TENANT || false) as boolean,
  LLM_ENABLE_REALTIME_VOICE_IN_COMPOSE: (g.LLM_ENABLE_REALTIME_VOICE_IN_COMPOSE || false) as boolean,
  LLM_SHOW_HUMAN_REVIEW_DISCLAIMER: (g.LLM_SHOW_HUMAN_REVIEW_DISCLAIMER || false) as boolean,
  LLM_DATA_ACCESS_CONSENT_HIDDEN: (g.LLM_DATA_ACCESS_CONSENT_HIDDEN || false) as boolean,
  METERING_ENABLE_AI_FEATURES_USAGE_CHARTING: (g.METERING_ENABLE_AI_FEATURES_USAGE_CHARTING || false) as boolean,
  METERING_ENABLE_AI_FEATURES_USAGE_CHARTING_V2: (g.METERING_ENABLE_AI_FEATURES_USAGE_CHARTING_V2 || false) as boolean,
  METERING_USE_V2_CREDIT_ENTITLEMENT: (g.METERING_USE_V2_CREDIT_ENTITLEMENT || false) as boolean,
  MIGRATION_ENABLE_CONFIG_MIGRATION: (g.MIGRATION_ENABLE_CONFIG_MIGRATION || false) as boolean,
  MODBIO_CUSTOM_SYNTAX_NAMES_BY_ID: (g.MODBIO_CUSTOM_SYNTAX_NAMES_BY_ID ?? {}) as Record<string, string>,
  MODBIO_ENABLE_COMPOSITE_ENTITIES: (g.MODBIO_ENABLE_COMPOSITE_ENTITIES || false) as boolean,
  MODBIO_ENABLE_CUSTOM_SYNTAXES: (g.MODBIO_ENABLE_CUSTOM_SYNTAXES ?? false) as boolean,
  MODBIO_NATURAL_MONOMER_IDS_BY_NATURAL_ANALOGUE: (g.MODBIO_NATURAL_MONOMER_IDS_BY_NATURAL_ANALOGUE ?? {}) as Partial<Record<MonomerNaturalAnalogue, string>>,
  MODBIO_THYMINE_MONOMER_IDS: (g.MODBIO_THYMINE_MONOMER_IDS ?? []) as Array<string>,
  MODBIO_URACIL_MONOMER_IDS: (g.MODBIO_URACIL_MONOMER_IDS ?? []) as Array<string>,
  MOLBIO_ALIGNMENT_CAPITALIZATION_QUALITY_THRESHOLD: g.MOLBIO_ALIGNMENT_CAPITALIZATION_QUALITY_THRESHOLD as number,
  MOLBIO_ALIGNMENT_TIMEOUT: g.MOLBIO_ALIGNMENT_TIMEOUT as number,
  MOLBIO_ALPHAFOLD_EXECUTION_COUNT_LIMIT: g.MOLBIO_ALPHAFOLD_EXECUTION_COUNT_LIMIT as number,
  MOLBIO_ALPHAFOLD_IS_ACADEMIC: (g.MOLBIO_ALPHAFOLD_IS_ACADEMIC || false) as boolean,
  MOLBIO_ENABLE_AA_PARTS: (g.MOLBIO_ENABLE_AA_PARTS || false) as boolean,
  MOLBIO_ENABLE_ALIGNMENT_UNDO: (g.MOLBIO_ENABLE_ALIGNMENT_UNDO || false) as boolean,
  MOLBIO_ENABLE_ALPHAFOLD_GA: (g.MOLBIO_ENABLE_ALPHAFOLD_GA || false) as boolean,
  MODEL_HUB_TAC_PERMISSIONS_ENABLED: (g.MODEL_HUB_TAC_PERMISSIONS_ENABLED || false) as boolean,
  MODEL_HUB_ENABLE_DATA_TAB: (g.MODEL_HUB_ENABLE_DATA_TAB || false) as boolean,
  MODEL_HUB_ENABLE_BOLTZ1: (g.MODEL_HUB_ENABLE_BOLTZ1 || false) as boolean,
  MODEL_HUB_ENABLE_GENERATIVE_MODELS_TAB: (g.MODEL_HUB_ENABLE_GENERATIVE_MODELS_TAB || false) as boolean,
  MODEL_HUB_ENABLE_MODEL_HUB_UI: (g.MODEL_HUB_ENABLE_MODEL_HUB_UI || false) as boolean,
  MODEL_HUB_ENABLE_OPENFOLD2: (g.MODEL_HUB_ENABLE_OPENFOLD2 || false) as boolean,
  MODEL_HUB_ENABLE_OPENFOLD3: (g.MODEL_HUB_ENABLE_OPENFOLD3 || false) as boolean,
  MODEL_HUB_ENABLE_PREDICTION_JOB_PREVIEW_COLUMN: (g.MODEL_HUB_ENABLE_PREDICTION_JOB_PREVIEW_COLUMN || false) as boolean,
  MODEL_HUB_ENABLE_PROTENIX: (g.MODEL_HUB_ENABLE_PROTENIX || false) as boolean,
  MODEL_HUB_ENABLE_STRUCTURE_PREDICTION_MODELS: (g.MODEL_HUB_ENABLE_STRUCTURE_PREDICTION_MODELS || false) as boolean,
  MODEL_HUB_IS_STANDALONE: (g.MODEL_HUB_IS_STANDALONE || false) as boolean,
  MODEL_HUB_MHS_ENABLED_MODELS: (g.MODEL_HUB_MHS_ENABLED_MODELS || []) as Array<string>,
  MODEL_HUB_SCIENTIFIC_AI_ENABLEMENT_STATE: (g.MODEL_HUB_SCIENTIFIC_AI_ENABLEMENT_STATE || 'AVAILABLE_DISABLED') as string,
  MODEL_HUB_SCIENTIFIC_AI_USER_TAC_ENABLED: (g.MODEL_HUB_SCIENTIFIC_AI_USER_TAC_ENABLED || false) as boolean,
  MOLBIO_ENABLE_ANTIBODY_ENGINEERING_TOOLS: Boolean(g.MOLBIO_ENABLE_ANTIBODY_ENGINEERING_TOOLS),
  MOLBIO_ENABLE_ANTIBODY_ENTITIES: (g.MOLBIO_ENABLE_ANTIBODY_ENTITIES || false) as boolean,
  MOLBIO_ENABLE_ANTIBODY_IN_PLACE_MIGRATION: (g.MOLBIO_ENABLE_ANTIBODY_IN_PLACE_MIGRATION || false) as boolean,
  MOLBIO_ENABLE_ANTIBODY_REGISTRATION_TABLE: (g.MOLBIO_ENABLE_ANTIBODY_REGISTRATION_TABLE || false) as boolean,
  MOLBIO_ENABLE_BULK_BACK_TRANSLATION: (g.MOLBIO_ENABLE_BULK_BACK_TRANSLATION || false) as boolean,
  MOLBIO_ENABLE_BULK_GIBSON_ASSEMBLY: (g.MOLBIO_ENABLE_BULK_GIBSON_ASSEMBLY || false) as boolean,
  MOLBIO_ENABLE_CIRCULAR_ALIGNMENT_ROTATION_OPT_OUT: (g.MOLBIO_ENABLE_CIRCULAR_ALIGNMENT_ROTATION_OPT_OUT || false) as boolean,
  MOLBIO_ENABLE_EDITING_BIOINTELLIGENT_LINKS: (g.MOLBIO_ENABLE_EDITING_BIOINTELLIGENT_LINKS || false) as boolean,
  MOLBIO_ENABLE_EXTERNAL_BLAST: Boolean(g.MOLBIO_ENABLE_EXTERNAL_BLAST),
  MOLBIO_ENABLE_EXTERNAL_BLAST_CONFIRMATION_MODAL: (g.MOLBIO_ENABLE_EXTERNAL_BLAST_CONFIRMATION_MODAL || false) as boolean,
  MOLBIO_ENABLE_AUTOFILL_IN_REG_TABLES: (g.MOLBIO_ENABLE_AUTOFILL_IN_REG_TABLES || false) as boolean,
  MOLBIO_ENABLE_IDT_SYNTAX: (g.MOLBIO_ENABLE_IDT_SYNTAX || false) as boolean,
  MOLBIO_ENABLE_JEF_CLUSTALO_ALIGNMENTS: (g.MOLBIO_ENABLE_JEF_CLUSTALO_ALIGNMENTS || false) as boolean,
  MOLBIO_ENABLE_JEF_MAFFT_ALIGNMENTS: (g.MOLBIO_ENABLE_JEF_MAFFT_ALIGNMENTS || false) as boolean,
  MOLBIO_ENABLE_MODIFIED_NUCLEOTIDES: (g.MOLBIO_ENABLE_MODIFIED_NUCLEOTIDES || false) as boolean,
  MOLBIO_ENABLE_UT_FLEXIBILITY: (g.MOLBIO_ENABLE_UT_FLEXIBILITY || false) as boolean,
  MOLBIO_ENFORCE_SEQUENCE_LENGTH_LIMITS: Boolean(g.MOLBIO_ENFORCE_SEQUENCE_LENGTH_LIMITS),
  MOLBIO_HIDE_ANALYZE_AA_SEQUENCES: Boolean(g.MOLBIO_HIDE_ANALYZE_AA_SEQUENCES) || false,
  MOLBIO_MONOSPACE_FONT_CORRECTION_AUTOFIX: Boolean(g.MOLBIO_MONOSPACE_FONT_CORRECTION_AUTOFIX) || false,
  MOLBIO_MONOSPACE_FONT_CORRECTION_WARN: Boolean(g.MOLBIO_MONOSPACE_FONT_CORRECTION_WARN) || false,
  MOLBIO_REDESIGNED_CLONING_FLOW: Boolean(g.MOLBIO_REDESIGNED_CLONING_FLOW) || false,
  NAVIGATION_USE_OPEN_FOLDER_AS_DEFAULT: (g.NAVIGATION_USE_OPEN_FOLDER_AS_DEFAULT || false) as boolean,
  NAV_ENABLE_CUSTOM_LOCALES: (g.NAV_ENABLE_CUSTOM_LOCALES || false) as boolean,
  NAV_ENABLE_FOLDER_UNGROUP_TOOL: (g.NAV_ENABLE_FOLDER_UNGROUP_TOOL || false) as boolean,
  NAV_ENABLE_LOCALE_AWARE_PARSING_FOR_SPREADSHEET_UPLOAD: (g.NAV_ENABLE_LOCALE_AWARE_PARSING_FOR_SPREADSHEET_UPLOAD || false) as boolean,
  NAV_ENABLE_SKIP_SPREADSHEET_ROWS_WITH_ERRORS: (g.NAV_ENABLE_SKIP_SPREADSHEET_ROWS_WITH_ERRORS ?? true) as boolean,
  NAV_ENABLE_TAB_URL_PARAM: (g.NAV_ENABLE_TAB_URL_PARAM || false) as boolean,
  NEW_HOMEPAGE_LA_OPT_IN: (g.NEW_HOMEPAGE_LA_OPT_IN || false) as boolean,
  ONBOARDING_MATERIALS_LINK: (g.ONBOARDING_MATERIALS_LINK || null) as string,
  PARTS_ENABLE_CROSS_ORIGIN_PARTS: (g.PARTS_ENABLE_CROSS_ORIGIN_PARTS || false) as boolean,
  PARTS_ENABLE_OVERLAPPING_PARTS: (g.PARTS_ENABLE_OVERLAPPING_PARTS || false) as boolean,
  PERF_APM_SAMPLING_RATE_WEB: g.PERF_APM_SAMPLING_RATE_WEB as number,
  PERF_ENABLE_JS_SELF_SAMPLING_PROFILER: (g.PERF_ENABLE_JS_SELF_SAMPLING_PROFILER || null) as JsSelfSamplingProfilerConfig | null,
  PERF_ENABLE_REAL_USER_MONITORING: Boolean(g.PERF_ENABLE_REAL_USER_MONITORING) || false,
  PERF_ENABLE_SESSION_REPLAY: (g.PERF_ENABLE_SESSION_REPLAY || false) as boolean,
  PERMISSIONS_DEFAULT_SCHEMA_POLICIES: (g.PERMISSIONS_DEFAULT_SCHEMA_POLICIES || null) as DefaultSchemaPoliciesConfig,
  PERMISSIONS_ENABLE_ASSAY_SCHEMA_PERMISSIONS: (g.PERMISSIONS_ENABLE_ASSAY_SCHEMA_PERMISSIONS || false) as boolean,
  PERMISSIONS_ENABLE_ASSAY_SCHEMA_REQUEST_PERMISSIONS: (g.PERMISSIONS_ENABLE_ASSAY_SCHEMA_REQUEST_PERMISSIONS || false) as boolean,
  PERMISSIONS_ENABLE_GRANULAR_ENTITY_PERMISSIONS: (g.PERMISSIONS_ENABLE_GRANULAR_ENTITY_PERMISSIONS || false) as boolean,
  PERMISSIONS_ENABLE_GRANULAR_ENTRY_PERMISSIONS: (g.PERMISSIONS_ENABLE_GRANULAR_ENTRY_PERMISSIONS || false) as boolean,
  PERMISSIONS_ENABLE_GRANULAR_FOLDER_PERMISSIONS: (g.PERMISSIONS_ENABLE_GRANULAR_FOLDER_PERMISSIONS ?? true) as boolean,
  PERMISSIONS_ENABLE_SCHEMA_ACCESS_POLICIES_SETTINGS_TAB: (g.PERMISSIONS_ENABLE_SCHEMA_ACCESS_POLICIES_SETTINGS_TAB || false) as boolean,
  PERMISSIONS_ENABLE_SCHEMA_POLICY_CONFIGURATION: (g.PERMISSIONS_ENABLE_SCHEMA_POLICY_CONFIGURATION || false) as boolean,
  PERMISSIONS_ENABLE_USER_ADMIN_ROLE: (g.PERMISSIONS_ENABLE_USER_ADMIN_ROLE || false) as boolean,
  PIPEBIO_ENABLE_TENANT_CREATION: (g.PIPEBIO_ENABLE_TENANT_CREATION || false) as boolean,
  MCP_ENABLE_TENANT_CREATION: (g.MCP_ENABLE_TENANT_CREATION || false) as boolean,
  PIPELINES_ENABLE_COLLAPSIBLE_DATA_CATALOG_ITEM_DETAILS: (g.PIPELINES_ENABLE_COLLAPSIBLE_DATA_CATALOG_ITEM_DETAILS ?? true) as boolean,
  PIPELINES_ENABLE_CONNECTION_INTEGRATION: (g.PIPELINES_ENABLE_CONNECTION_INTEGRATION || false) as boolean,
  PIPELINES_MAX_STEP_COUNT: (g.PIPELINES_MAX_STEP_COUNT || 600) as number,
  POLICIES_DEFAULT_TEAM_ADMINS_TO_COLLABORATOR: (g.POLICIES_DEFAULT_TEAM_ADMINS_TO_COLLABORATOR || false) as boolean,
  PREVENT_REVERT_PAST_DATA_SUBMIT_ACTION: (g.PREVENT_REVERT_PAST_DATA_SUBMIT_ACTION || false) as boolean,
  PRIVATE_DEPLOY: (g.PRIVATE_DEPLOY || false) as boolean,
  PROCEDURES_ENABLE: (g.PROCEDURES_ENABLE || false) as boolean,
  PROCEDURES_ENABLE_ANALYSIS_TEMPLATE_INTEGRATION: (g.PROCEDURES_ENABLE_ANALYSIS_TEMPLATE_INTEGRATION || false) as boolean,
  PROCEDURES_ENABLE_ASSAYS: (g.PROCEDURES_ENABLE_ASSAYS || false) as boolean,
  PROCEDURES_ENABLE_DROPDOWN_DATA_PARAMETER: (g.PROCEDURES_ENABLE_DROPDOWN_DATA_PARAMETER || false) as boolean,
  PROCEDURES_ENABLE_SEND_TO_ANALYSIS: (g.PROCEDURES_ENABLE_SEND_TO_ANALYSIS || false) as boolean,
  PROCEDURES_ENABLE_PUBLISHING: (g.PROCEDURES_ENABLE_PUBLISHING || false) as boolean,
  PROCEDURES_ENABLE_USE_PARAMETERS_IN_PLATES: (g.PROCEDURES_ENABLE_USE_PARAMETERS_IN_PLATES || false) as boolean,
  PROCEDURES_ENABLE_WORKFLOWS_ORCHESTRATION: (g.PROCEDURES_ENABLE_WORKFLOWS_ORCHESTRATION || false) as boolean,
  PROCEDURES_ENABLE_PARAMETER_LIBRARY: (g.PROCEDURES_ENABLE_PARAMETER_LIBRARY || false) as boolean,
  PROCEDURES_ENABLE_PARAMETER_LIBRARY_USE_AND_ENFORCEMENT: (g.PROCEDURES_ENABLE_PARAMETER_LIBRARY_USE_AND_ENFORCEMENT || false) as boolean,
  PROCEDURES_PERF_MONITORING_NUM_TASKS_BUCKETS: (g.PROCEDURES_PERF_MONITORING_NUM_TASKS_BUCKETS || []) as Array<number>,
  PROCEDURES_RUN_PARAMETER_VALIDATIONS: (g.PROCEDURES_RUN_PARAMETER_VALIDATIONS || false) as boolean,
  REACTFLOW_FIT_VIEW_ANIMATION_DURATION_MS: (g.REACTFLOW_FIT_VIEW_ANIMATION_DURATION_MS ?? 800) as number,
  REACTFLOW_ZOOM_ANIMATION_DURATION_MS: (g.REACTFLOW_ZOOM_ANIMATION_DURATION_MS ?? 300) as number,
  REGISTRATION_TABLE_ENABLE_LIVE_TAGS: (g.REGISTRATION_TABLE_ENABLE_LIVE_TAGS || false) as boolean,
  REGISTRY_ENABLE_ASYNC_VALIDATION_SETTINGS_TAB: (g.REGISTRY_ENABLE_ASYNC_VALIDATION_SETTINGS_TAB || false) as boolean,
  REGISTRY_ENABLE_BULK_ENTITY_FOLDER_CHANGE: (g.REGISTRY_ENABLE_BULK_ENTITY_FOLDER_CHANGE || false) as boolean,
  REGISTRY_ENABLE_CHILD_ENTITIES: (g.REGISTRY_ENABLE_CHILD_ENTITIES || false) as boolean,
  REGISTRY_ENABLE_ENTITY_MAP_FILTERING: (g.REGISTRY_ENABLE_ENTITY_MAP_FILTERING || false) as boolean,
  REGISTRY_ENABLE_ENTITY_MAP_TAB: (g.REGISTRY_ENABLE_ENTITY_MAP_TAB || false) as boolean,
  REGISTRY_ENABLE_GENERATE_RANDOM_REGISTRY_IDS: (g.REGISTRY_ENABLE_GENERATE_RANDOM_REGISTRY_IDS || false) as boolean,
  REGISTRY_ENABLE_SAMPLE_INGESTION: (g.REGISTRY_ENABLE_SAMPLE_INGESTION || false) as boolean,
  REGISTRY_ENABLE_TRIAL_SAMPLES: (g.REGISTRY_ENABLE_TRIAL_SAMPLES || false) as boolean,
  REQUESTS_ENABLE_REQUESTOR: (g.REQUESTS_ENABLE_REQUESTOR || false) as boolean,
  REQUESTS_ENABLE_WORKFLOW_MIGRATION: (g.REQUESTS_ENABLE_WORKFLOW_MIGRATION || false) as boolean,
  REQUESTS_MAX_SAMPLE_GROUPS_PER_REQUEST: g.REQUESTS_MAX_SAMPLE_GROUPS_PER_REQUEST as number,
  REQUESTS_V2_ENABLE: (g.REQUESTS_V2_ENABLE || false) as boolean,
  REQUESTS_V2_MAX_NUMBER_OF_ASSIGNEES_TO_DISPLAY: g.REQUESTS_V2_MAX_NUMBER_OF_ASSIGNEES_TO_DISPLAY as number,
  REQUESTS_V2_MAX_NUMBER_OF_RECENTLY_USED_REQUEST_DEFINITIONS_TO_DISPLAY: g.REQUESTS_V2_MAX_NUMBER_OF_RECENTLY_USED_REQUEST_DEFINITIONS_TO_DISPLAY as number,
  REQUESTS_V2_MAX_REQUEST_TEMPLATES_PER_TASK_SCHEMA: g.REQUESTS_V2_MAX_REQUEST_TEMPLATES_PER_TASK_SCHEMA as number,
  RESULTS_ENABLE_CREATE_RESULT_LINK_FIELDS: (g.RESULTS_ENABLE_CREATE_RESULT_LINK_FIELDS || false) as boolean,
  RESULTS_ENABLE_RESULTS_AGAINST_CONTAINERS_FEATURES: (g.RESULTS_ENABLE_RESULTS_AGAINST_CONTAINERS_FEATURES || false) as boolean,
  RESULTS_FILTER_TO_REVIEWED_DATA_OUTSIDE_ENTRY: (g.RESULTS_FILTER_TO_REVIEWED_DATA_OUTSIDE_ENTRY || false) as boolean,
  RESULTS_SEPARATE_PUBLISHED_DATA_TABLE_COLUMNS_LOAD: (g.RESULTS_SEPARATE_PUBLISHED_DATA_TABLE_COLUMNS_LOAD || 'OFF') as 'OFF' | 'ON' | 'DUAL_READ',
  RESULTS_SHOW_ARCHIVED_SCHEMAS_ON_RESULTS_TAB: (g.RESULTS_SHOW_ARCHIVED_SCHEMAS_ON_RESULTS_TAB || false) as boolean,
  SAMPLE_TESTS_ENABLE: (g.SAMPLE_TESTS_ENABLE || false) as boolean,
  SAMPLE_TESTS_ENABLE_MATRIX_PLATE_TEST_ORDER_AUTOMATION_WORKAROUND: (g.SAMPLE_TESTS_ENABLE_MATRIX_PLATE_TEST_ORDER_AUTOMATION_WORKAROUND || false) as boolean,
  SAMPLE_TYPES_ENABLE: (g.SAMPLE_TYPES_ENABLE || false) as boolean,
  SAVE_ENTRY_CREATION_CONTEXT: (g.SAVE_ENTRY_CREATION_CONTEXT as boolean | undefined) ?? false,
  SCHEMAS_DECIMAL_ROUNDING_STRATEGY: (g.SCHEMAS_DECIMAL_ROUNDING_STRATEGY || DecimalRoundingStrategy.ROUND_HALF_UP) as DecimalRoundingStrategy,
  SCHEMAS_ENABLE_CHANGE_WAREHOUSE_NAME: (g.SCHEMAS_ENABLE_CHANGE_WAREHOUSE_NAME || false) as boolean,
  SCHEMAS_ENABLE_COMPOSABLE_COMPUTED_FIELDS: (g.SCHEMAS_ENABLE_COMPOSABLE_COMPUTED_FIELDS || false) as boolean,
  SCHEMAS_ENABLE_COMPUTED_FIELDS_CONFIGURATION_DISPLAY: (g.SCHEMAS_ENABLE_COMPUTED_FIELDS_CONFIGURATION_DISPLAY || false) as boolean,
  SCHEMAS_ENABLE_COMPUTED_FIELDS_OVER_RESULTS: (g.SCHEMAS_ENABLE_COMPUTED_FIELDS_OVER_RESULTS || false) as boolean,
  SCHEMAS_ENABLE_DATA_MODEL_DIAGRAM: (g.SCHEMAS_ENABLE_DATA_MODEL_DIAGRAM || false) as boolean,
  SCHEMAS_ENABLE_DUPLICATE_TAG_SCHEMA: (g.SCHEMAS_ENABLE_DUPLICATE_TAG_SCHEMA || false) as boolean,
  SCHEMAS_ENABLE_INTEGRATION_FIELDS: (g.SCHEMAS_ENABLE_INTEGRATION_FIELDS || false) as boolean,
  SCHEMAS_ENABLE_INTERFACE_FIELD_TOOLTIP_EDITING: (g.SCHEMAS_ENABLE_INTERFACE_FIELD_TOOLTIP_EDITING || false) as boolean,
  SCHEMAS_ENABLE_INTERFACE_FIELD_UNITS: (g.SCHEMAS_ENABLE_INTERFACE_FIELD_UNITS || false) as boolean,
  SCHEMAS_ENABLE_SCHEMA_DESCRIPTION_FOR_SCHEMA_INTERFACES: (g.SCHEMAS_ENABLE_SCHEMA_DESCRIPTION_FOR_SCHEMA_INTERFACES || false) as boolean,
  SCHEMAS_ENABLE_SCHEMA_FIELD_DESCRIPTION_FOR_TABLES_SUPPORTED_SCHEMAS: (g.SCHEMAS_ENABLE_SCHEMA_FIELD_DESCRIPTION_FOR_TABLES_SUPPORTED_SCHEMAS || false) as boolean,
  SCHEMAS_ENABLE_SCHEMA_FIELD_MIGRATION: (g.SCHEMAS_ENABLE_SCHEMA_FIELD_MIGRATION || false) as boolean,
  SCHEMAS_ENABLE_MANUAL_SCHEMA_FIELD_MIGRATION: (g.SCHEMAS_ENABLE_MANUAL_SCHEMA_FIELD_MIGRATION || false) as boolean,
  SCHEMAS_ENABLE_SCHEMA_FIELD_MIGRATION_TO_EXISTING_FIELDS: (g.SCHEMAS_ENABLE_SCHEMA_FIELD_MIGRATION_TO_EXISTING_FIELDS || false) as boolean,
  SCHEMAS_ENABLE_SERIALIZATION_CACHING: (g.SCHEMAS_ENABLE_SERIALIZATION_CACHING || false) as boolean,
  SCHEMAS_ENABLE_UNEDITABLE_FIELDS_CONFIGURATION: (g.SCHEMAS_ENABLE_UNEDITABLE_FIELDS_CONFIGURATION || false) as boolean,
  SCHEMAS_ENABLE_VIEW_SYSTEM_NAME: (g.SCHEMAS_ENABLE_VIEW_SYSTEM_NAME || false) as boolean,
  SCHEMAS_NUM_SCHEMATIZED_ITEMS_SFM_THRESHOLD: g.SCHEMAS_NUM_SCHEMATIZED_ITEMS_SFM_THRESHOLD as number,
  SCHEMAS_LIMIT_FOR_UPDATE_TAG_SCHEMA_ITEM_COUNT: g.SCHEMAS_LIMIT_FOR_UPDATE_TAG_SCHEMA_ITEM_COUNT as number,
  SCHEMAS_RENAME_INTERFACE_TO_FIELDSET_AND_CATEGORY: (g.SCHEMAS_RENAME_INTERFACE_TO_FIELDSET_AND_CATEGORY ?? true) as boolean, // TODO (BNCH-96993): Remove flag
  SHOW_VALIDATED_CLOUD_PATCH_VERSION: (g.SHOW_VALIDATED_CLOUD_PATCH_VERSION ?? false) as boolean,
  SEARCH_ENABLE_AI_ASSISTED_SEARCH: (g.SEARCH_ENABLE_AI_ASSISTED_SEARCH || false) as boolean,
  SEARCH_ENABLE_OMNIBOX_VECTOR_SEARCH: (g.SEARCH_ENABLE_OMNIBOX_VECTOR_SEARCH || false) as boolean,
  SEARCH_ENABLE_SPLIT_ITEM_LISTING_QUERY: (g.SEARCH_ENABLE_SPLIT_ITEM_LISTING_QUERY || false) as boolean,
  SEGMENT_WRITE_KEY: g.SEGMENT_WRITE_KEY as string,
  SIGHTGLASS2_ENABLE_ANNOTATION_SEARCH: (g.SIGHTGLASS2_ENABLE_ANNOTATION_SEARCH || false) as boolean,
  STACK_NAME: (g.STACK_NAME || null) as string,
  START_BANNER_CONFIG: (g.START_BANNER_CONFIG || null) as CustomBannerConfig,
  STRUCTURED_TABLE_ENABLE_VALIDATION_BYPASS: (g.STRUCTURED_TABLE_ENABLE_VALIDATION_BYPASS || false) as boolean,
  STRUCTURED_TABLE_VALIDATION_BATCH_SIZE: g.STRUCTURED_TABLE_VALIDATION_BATCH_SIZE as number,
  STUDIES_ENABLE: (g.STUDIES_ENABLE || false) as boolean,
  STUDIES_ENABLE_ANALYSIS_DATA_REFRESH: (g.STUDIES_ENABLE_ANALYSIS_DATA_REFRESH || false) as boolean,
  STUDIES_ENABLE_CROSS_STUDY_ANALYSIS: (g.STUDIES_ENABLE_CROSS_STUDY_ANALYSIS || false) as boolean,
  STUDIES_ENABLE_FLOWCHART_PROCEDURE_RUN_DESIGN: (g.STUDIES_ENABLE_FLOWCHART_PROCEDURE_RUN_DESIGN || false) as boolean,
  STUDIES_ENABLE_ASSAY_DEVELOPMENT: (g.STUDIES_ENABLE_ASSAY_DEVELOPMENT || false) as boolean,
  STUDIES_ENABLE_CLINICAL: (g.STUDIES_ENABLE_CLINICAL || false) as boolean,
  STUDIES_ENABLE_CLINICAL_PROCEDURE_PUBLISHING: (g.STUDIES_ENABLE_CLINICAL_PROCEDURE_PUBLISHING || false) as boolean,
  STUDIES_ENABLE_OFFLINE_RESULTS_IN_ANALYSIS: (g.STUDIES_ENABLE_OFFLINE_RESULTS_IN_ANALYSIS || false) as boolean,
  STUDIES_ENABLE_PLATE_AND_LAB_AUTO_DATASETS_IN_ANALYSIS: (g.STUDIES_ENABLE_PLATE_AND_LAB_AUTO_DATASETS_IN_ANALYSIS || false) as boolean,
  STUDIES_ENABLE_PRECLINICAL: (g.STUDIES_ENABLE_PRECLINICAL || false) as boolean,
  STUDIES_ENABLE_PROCEDURE_RUN_DESIGN: (g.STUDIES_ENABLE_PROCEDURE_RUN_DESIGN || false) as boolean,
  STUDIES_ENABLE_PROCEDURE_RUN_PLAN_LLM_INTEGRATION: (g.STUDIES_ENABLE_PROCEDURE_RUN_PLAN_LLM_INTEGRATION || false) as boolean,
  STUDIES_ENABLE_RESULTS: (g.STUDIES_ENABLE_RESULTS || {
    [StudyType.CLINICAL]: false,
    [StudyType.PRECLINICAL]: false,
  }) as Partial<Record<StudyType, boolean>>,
  STUDIES_ENABLE_REVIEW: (g.STUDIES_ENABLE_REVIEW || false) as boolean,
  STUDIES_MAX_NUM_EXPERIMENTS_PER_ASSAY_DEV_STUDY: g.STUDIES_MAX_NUM_EXPERIMENTS_PER_ASSAY_DEV_STUDY as number,
  STUDIES_MAX_NUM_RUNS_PER_PD_STUDY: g.STUDIES_MAX_NUM_RUNS_PER_PD_STUDY as number,
  STUDIES_PER_CROSS_STUDY_ANALYSIS_LIMIT: g.STUDIES_PER_CROSS_STUDY_ANALYSIS_LIMIT as number,
  SUPPORT_CUSTOM_HELP_OPTION: (g.SUPPORT_CUSTOM_HELP_OPTION || null) as CustomHelpOption,
  SUPPORT_EMAIL_ADDRESS: (g.SUPPORT_EMAIL_ADDRESS || false) as boolean,
  SUPPORT_SITE_URL: (g.SUPPORT_SITE_URL || null) as string,
  TEMP_UPDATED_MIXTURE_PREP_EXPORT_STYLE: (g.TEMP_UPDATED_MIXTURE_PREP_EXPORT_STYLE || false) as boolean,
  TENANT_DOMAIN: (g.TENANT_DOMAIN || null) as string,
  TENANT_HAS_SAML: (g.TENANT_HAS_SAML || null) as string,
  TENANT_ID: (g.TENANT_ID || null) as string,
  TENANT_IS_SUPER: g.TENANT_IS_SUPER as boolean,
  TENANT_NAME: (g.TENANT_NAME || null) as string,
  TENANT_SUBDOMAIN: g.TENANT_SUBDOMAIN as string,
  TFI_ENABLE: (g.TFI_ENABLE || false) as boolean,
  TFI_ENABLE_BOX: (g.TFI_ENABLE_BOX || false) as boolean,
  TFI_ENABLE_DROPBOX: (g.TFI_ENABLE_DROPBOX || false) as boolean,
  TFI_ENABLE_EGNYTE: (g.TFI_ENABLE_EGNYTE || false) as boolean,
  TFI_ENABLE_GDRIVE: (g.TFI_ENABLE_GDRIVE || false) as boolean,
  TFI_ENABLE_ONEDRIVE: (g.TFI_ENABLE_ONEDRIVE || false) as boolean,
  VALIDATED_CLOUD_VERSION: (g.VALIDATED_CLOUD_VERSION || null) as string,
  VALIDATION_RULES_ENGINE_ENABLE_UI_PROCESS_TASK_SCHEMAS: (g.VALIDATION_RULES_ENGINE_ENABLE_UI_PROCESS_TASK_SCHEMAS || false) as boolean,
  VALIDATION_RULES_ENGINE_ENABLE_M3: (g.VALIDATION_RULES_ENGINE_ENABLE_M3 || false) as boolean,
  VALIDATION_RULES_ENGINE_ENABLE_TAG_SCHEMAS: (g.VALIDATION_RULES_ENGINE_ENABLE_TAG_SCHEMAS || false) as boolean,
  WAREHOUSE_ALLOW_USER_DIRECT_ACCESS: (g.WAREHOUSE_ALLOW_USER_DIRECT_ACCESS || false) as boolean,
  WORKFLOWS_DISABLE_CHANGING_TASK_GROUP_PREFIX: (g.WORKFLOWS_DISABLE_CHANGING_TASK_GROUP_PREFIX || false) as boolean,
  WORKFLOWS_ENABLE: (g.WORKFLOWS_ENABLE || false) as boolean,
  WORKFLOWS_ENABLE_ARCHIVE_VALIDATION: (g.WORKFLOWS_ENABLE_ARCHIVE_VALIDATION || false) as boolean,
  WORKFLOWS_ENABLE_DASHBOARD_EXECUTION_TASK_GROUP_ORDERING: g.WORKFLOWS_ENABLE_DASHBOARD_EXECUTION_TASK_GROUP_ORDERING as boolean,
  WORKFLOWS_ENABLE_FLOWCHART_ZERO_FIELD_MAPPERS: (g.WORKFLOWS_ENABLE_FLOWCHART_ZERO_FIELD_MAPPERS || false) as boolean,
  WORKFLOWS_ENABLE_NOTIFICATIONS: (g.WORKFLOWS_ENABLE_NOTIFICATIONS || false) as boolean,
  WORKFLOWS_ENABLE_PIPELINE_NODES: (g.WORKFLOWS_ENABLE_PIPELINE_NODES || false) as boolean,
  WORKFLOWS_IMPROVED_FLOWCHART_CONFIG: (g.WORKFLOWS_IMPROVED_FLOWCHART_CONFIG || false) as boolean,
  WORKFLOWS_MAX_CONDITIONS_PER_ROUTER: g.WORKFLOWS_MAX_CONDITIONS_PER_ROUTER as number,
  WORKFLOWS_MAX_DATA_FLOW_EDGES_VISIBLE_IN_UI: g.WORKFLOWS_MAX_DATA_FLOW_EDGES_VISIBLE_IN_UI as number,
  WORKFLOWS_MAX_EXPRESSIONS_PER_ROUTER: g.WORKFLOWS_MAX_EXPRESSIONS_PER_ROUTER as number,
  WORKFLOWS_MAX_DIRECT_EXECUTION_TASKS: g.WORKFLOWS_MAX_DIRECT_EXECUTION_TASKS as number,
  WORKFLOWS_MAX_ITEMS_PER_BULK_ACTION: g.WORKFLOWS_MAX_ITEMS_PER_BULK_ACTION as number,
  WORKFLOWS_MAX_NODES_PER_FLOWCHART_CAN_BE_RENDERED_IN_UI: g.WORKFLOWS_MAX_NODES_PER_FLOWCHART_CAN_BE_RENDERED_IN_UI as number,
  WORKFLOWS_MAX_ROWS_IN_WORKSPACE_TAB_TABLE_BEFORE_BATCHING: g.WORKFLOWS_MAX_ROWS_IN_WORKSPACE_TAB_TABLE_BEFORE_BATCHING as number,
  WORKFLOWS_MAX_ROWS_FOR_LEGACY_CONFIRMATION_MODALS: (g.WORKFLOWS_MAX_ROWS_FOR_LEGACY_CONFIRMATION_MODALS ?? 500) as number,
  WORKFLOWS_MAX_SNAPSHOT_FIELDS_PER_SCHEMA: g.WORKFLOWS_MAX_SNAPSHOT_FIELDS_PER_SCHEMA as number,
  WORKFLOWS_MAX_TASKS_FOR_SYNCHRONOUS_BULK_TASK_COPY: g.WORKFLOWS_MAX_TASKS_FOR_SYNCHRONOUS_BULK_TASK_COPY as number,
  WORKFLOWS_MAX_TASKS_FOR_SYNCHRONOUS_DIRECT_TASK_COMPLETION: g.WORKFLOWS_MAX_TASKS_FOR_SYNCHRONOUS_DIRECT_TASK_COMPLETION as number,
  WORKFLOWS_MAX_TASKS_FOR_SYNCHRONOUS_ENTRY_EXECUTION: g.WORKFLOWS_MAX_TASKS_FOR_SYNCHRONOUS_ENTRY_EXECUTION as number,
  WORKFLOWS_MAX_TASKS_FOR_SYNCHRONOUS_TASKGROUP_CREATION: g.WORKFLOWS_MAX_TASKS_FOR_SYNCHRONOUS_TASKGROUP_CREATION as number,
  WORKFLOWS_MAX_TASKS_PER_ENTRY: g.WORKFLOWS_MAX_TASKS_PER_ENTRY as number,
  WORKFLOWS_MAX_TASKS_PER_TASK_GROUP: g.WORKFLOWS_MAX_TASKS_PER_TASK_GROUP as number,
  WORKFLOWS_MAX_UNARCHIVED_FIELDS_PER_SCHEMA: g.WORKFLOWS_MAX_UNARCHIVED_FIELDS_PER_SCHEMA as number,
  WORKFLOWS_NUM_ROWS_PER_BATCH_IN_WORKSPACE_TAB_TABLE_SUBMISSION: g.WORKFLOWS_NUM_ROWS_PER_BATCH_IN_WORKSPACE_TAB_TABLE_SUBMISSION as number,
  WORKLISTS_ENABLE_WORKLISTS: (g.WORKLISTS_ENABLE_WORKLISTS || false) as boolean,
  WORKLISTS_MAX_ITEMS_PER_WORKLIST: g.WORKLISTS_MAX_ITEMS_PER_WORKLIST as number,
  WORKSHEET_REVIEW_REQUIRES_SUCCESSFUL_SNAPSHOT: (g.WORKSHEET_REVIEW_REQUIRES_SUCCESSFUL_SNAPSHOT || false) as boolean,
  WORKSHEET_ENABLE_SNAPSHOT_NOTIFICATIONS: (g.WORKSHEET_ENABLE_SNAPSHOT_NOTIFICATIONS || false) as boolean,
  WORKSHEET_SEND_REVIEW_REQUIRES_SUBMITTED_TABLES: (g.WORKSHEET_SEND_REVIEW_REQUIRES_SUBMITTED_TABLES || false) as boolean,
};

const defaultConfig = deepCopy(BenchlingConfig);

export default BenchlingConfig;

// Keep keys in sync with benchling/lib/react_render.py:_get_benchling_config
export interface ServerBootstrapData {
  ENABLE_ANALYSIS_TOOL_WITHOUT_INSIGHTS: boolean;
  ELN_MAX_STRUCTURED_TABLE_PERSISTED_ROWS: number;
  ELN_ENABLE_IDS_IN_ENTRY_CHIPS: boolean;
  ELN_ENABLE_SMART_CHIP_SNAPSHOT_CONTEXT: boolean;
  ELN_ENABLE_CROSS_STEP_DATA_REFS: boolean;
  ELN_ENABLE_COLUMN_TYPE_ICONS: boolean;
  NAV_ENABLE_CUSTOM_LOCALES: boolean;
  CHEMISTRY_ENABLE_MOLECULE: boolean;
  ELN_EXPORT_REVIEW_HISTORY_UI_VERSION: number;
  ELN_EXPORT_INCLUDE_TENANT_AND_API_ID: boolean;
  ELN_EXPORT_PDF_CONFIG: ExportPdfConfig;
  ELN_EXPORT_USE_APHRODITE_WITH_NODESHELL: boolean;
  ELN_EXPORT_SNAPSHOT_CHIPS_ALLOW_LIST: SnapshotChipsAllowList;
  ELN_ENABLE_ENTRY_SECTIONS: boolean;
  ENABLE_FROZEN_CHIPS: boolean;
  ENABLE_GAINSIGHT_PX: boolean;
  ELN_RENDER_PRINT_PREVIEW_METADATA: boolean;
  ELN_INLINE_TABLE_VIEW_CELL_LIMIT: number;
  ELN_INLINE_TABLE_VIEW_ROW_LIMIT: number;
  TEMP_UPDATED_MIXTURE_PREP_EXPORT_STYLE: boolean;
  ARCHIVE_RUN_AND_RESULT_PURPOSES: Array<string>;
  ARCHIVE_NON_DATA_PURPOSES: Array<string>;
  ARCHIVE_EXPENDABLE_PURPOSES: Array<string>;
  ARCHIVE_NON_EXPENDABLE_PURPOSES: Array<string>;

  INVENTORY_ENABLE_SOLID_UNITS_FOR_CONTAINER: boolean;
  ENABLE_LAB_AUTOMATION_FILE_CHUNKING: boolean;
  STUDIES_ENABLE: boolean;
  REQUESTS_V2_ENABLE: boolean;
}

export const SERVER_BOOTSTRAP_KEYS: Array<keyof ServerBootstrapData> = [
  'ENABLE_ANALYSIS_TOOL_WITHOUT_INSIGHTS',
  'ELN_MAX_STRUCTURED_TABLE_PERSISTED_ROWS',
  'ELN_ENABLE_IDS_IN_ENTRY_CHIPS',
  'ELN_ENABLE_SMART_CHIP_SNAPSHOT_CONTEXT',
  'ELN_ENABLE_COLUMN_TYPE_ICONS',
  'NAV_ENABLE_CUSTOM_LOCALES',
  'ELN_ENABLE_CROSS_STEP_DATA_REFS',
  'CHEMISTRY_ENABLE_MOLECULE',
  'ELN_EXPORT_REVIEW_HISTORY_UI_VERSION',
  'ELN_EXPORT_INCLUDE_TENANT_AND_API_ID',
  'ELN_EXPORT_PDF_CONFIG',
  'ELN_EXPORT_USE_APHRODITE_WITH_NODESHELL',
  'ELN_EXPORT_SNAPSHOT_CHIPS_ALLOW_LIST',
  'ELN_ENABLE_ENTRY_SECTIONS',
  'ENABLE_FROZEN_CHIPS',
  'ENABLE_GAINSIGHT_PX',
  'ELN_RENDER_PRINT_PREVIEW_METADATA',
  'ELN_INLINE_TABLE_VIEW_CELL_LIMIT',
  'ELN_INLINE_TABLE_VIEW_ROW_LIMIT',
  'TEMP_UPDATED_MIXTURE_PREP_EXPORT_STYLE',
  'ARCHIVE_RUN_AND_RESULT_PURPOSES',
  'ARCHIVE_NON_DATA_PURPOSES',
  'ARCHIVE_EXPENDABLE_PURPOSES',
  'ARCHIVE_NON_EXPENDABLE_PURPOSES',

  'INVENTORY_ENABLE_SOLID_UNITS_FOR_CONTAINER',
  'ENABLE_LAB_AUTOMATION_FILE_CHUNKING',
  'STUDIES_ENABLE',
  'REQUESTS_V2_ENABLE',
];

export function __serverBootstrap(data: ServerBootstrapData): void {
  const keys = Object.keys(data);
  // Sort to ignore order
  if (!isEqual(_.sortBy(SERVER_BOOTSTRAP_KEYS), _.sortBy(keys))) {
    throw new Error(`Unexpected server bootstrap keys: ${keys}`);
  }

  BenchlingConfig.ENABLE_ANALYSIS_TOOL_WITHOUT_INSIGHTS = data.ENABLE_ANALYSIS_TOOL_WITHOUT_INSIGHTS;
  BenchlingConfig.ELN_MAX_STRUCTURED_TABLE_PERSISTED_ROWS = data.ELN_MAX_STRUCTURED_TABLE_PERSISTED_ROWS;
  BenchlingConfig.ELN_ENABLE_IDS_IN_ENTRY_CHIPS = data.ELN_ENABLE_IDS_IN_ENTRY_CHIPS;
  BenchlingConfig.ELN_ENABLE_SMART_CHIP_SNAPSHOT_CONTEXT = data.ELN_ENABLE_SMART_CHIP_SNAPSHOT_CONTEXT;
  BenchlingConfig.ELN_ENABLE_COLUMN_TYPE_ICONS = data.ELN_ENABLE_COLUMN_TYPE_ICONS;
  BenchlingConfig.NAV_ENABLE_CUSTOM_LOCALES = data.NAV_ENABLE_CUSTOM_LOCALES;
  BenchlingConfig.ELN_ENABLE_CROSS_STEP_DATA_REFS = data.ELN_ENABLE_CROSS_STEP_DATA_REFS;
  BenchlingConfig.CHEMISTRY_ENABLE_MOLECULE = data.CHEMISTRY_ENABLE_MOLECULE;
  BenchlingConfig.ELN_EXPORT_REVIEW_HISTORY_UI_VERSION = data.ELN_EXPORT_REVIEW_HISTORY_UI_VERSION;
  BenchlingConfig.ELN_EXPORT_INCLUDE_TENANT_AND_API_ID = data.ELN_EXPORT_INCLUDE_TENANT_AND_API_ID;
  BenchlingConfig.ELN_EXPORT_PDF_CONFIG = data.ELN_EXPORT_PDF_CONFIG;
  BenchlingConfig.ELN_EXPORT_USE_APHRODITE_WITH_NODESHELL = data.ELN_EXPORT_USE_APHRODITE_WITH_NODESHELL;
  BenchlingConfig.ELN_EXPORT_SNAPSHOT_CHIPS_ALLOW_LIST = data.ELN_EXPORT_SNAPSHOT_CHIPS_ALLOW_LIST;
  BenchlingConfig.ELN_ENABLE_ENTRY_SECTIONS = data.ELN_ENABLE_ENTRY_SECTIONS;
  BenchlingConfig.ENABLE_FROZEN_CHIPS = data.ENABLE_FROZEN_CHIPS;
  BenchlingConfig.ENABLE_GAINSIGHT_PX = data.ENABLE_GAINSIGHT_PX;
  BenchlingConfig.ELN_RENDER_PRINT_PREVIEW_METADATA = data.ELN_RENDER_PRINT_PREVIEW_METADATA;
  BenchlingConfig.ELN_INLINE_TABLE_VIEW_ROW_LIMIT = data.ELN_INLINE_TABLE_VIEW_ROW_LIMIT;
  BenchlingConfig.TEMP_UPDATED_MIXTURE_PREP_EXPORT_STYLE = data.TEMP_UPDATED_MIXTURE_PREP_EXPORT_STYLE;
  BenchlingConfig.ARCHIVE_RUN_AND_RESULT_PURPOSES = data.ARCHIVE_RUN_AND_RESULT_PURPOSES;
  BenchlingConfig.ARCHIVE_NON_DATA_PURPOSES = data.ARCHIVE_NON_DATA_PURPOSES;
  BenchlingConfig.ARCHIVE_EXPENDABLE_PURPOSES = data.ARCHIVE_EXPENDABLE_PURPOSES;
  BenchlingConfig.ARCHIVE_NON_EXPENDABLE_PURPOSES = data.ARCHIVE_NON_EXPENDABLE_PURPOSES;

  BenchlingConfig.INVENTORY_ENABLE_SOLID_UNITS_FOR_CONTAINER = data.INVENTORY_ENABLE_SOLID_UNITS_FOR_CONTAINER;
  BenchlingConfig.ENABLE_LAB_AUTOMATION_FILE_CHUNKING = data.ENABLE_LAB_AUTOMATION_FILE_CHUNKING;
  BenchlingConfig.STUDIES_ENABLE = data.STUDIES_ENABLE;
  BenchlingConfig.REQUESTS_V2_ENABLE = data.REQUESTS_V2_ENABLE;
}

export function resetBenchlingConfigToDefaultValuesForTests(): void {
  // Only reset to default values in test environment
  assert(Boolean(window.debug || global.__NODE_TESTING__), 'Expected to be called only in a test environment');
  Object.assign(BenchlingConfig, defaultConfig);
}
